import { CommonModule } from "@angular/common";
import { Component, inject, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Base64 } from "base64-string";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTabsModule } from "ng-zorro-antd/tabs";
import {
  catchError,
  EMPTY,
  expand,
  forkJoin,
  interval,
  Subject,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  timer,
} from "rxjs";
import { ConfigService } from "../../../core/services/config/config.service";
import { AccordionSection } from "../../../components/common/accordion-table-view/accordion-table-view.component";
import { AgenticChatComponent } from "../../../components/common/agentic-chat/agentic-chat.component";
import { EditableTableComponent } from "../../../components/common/editable-table/editable-table.component";
import { ExpandTableComponentComponent } from "../../../components/common/expand-table-component/expand-table-component.component";
import { ExpandableAccordionComponent } from "../../../components/common/expandable-accordion/expandable-accordion.component";
import { MultiTabTableComponent } from "../../../components/common/multi-tab-table/multi-tab-table.component";
import { UiObjectTableComponent } from "../../../components/common/ui-object-table/ui-object-table.component";
import { ExecutionCycleStatusCardComponent } from "../../../components/execution-results-view/execution-cycle-status-card/execution-cycle-status-card.component";
import { AgentSetupInfoComponent } from "../../../components/modal-templates/agent-setup-info/agent-setup-info.component";
import { DeviceInfoModalComponent } from "../../../components/modal-templates/device-info-modal/device-info-modal.component";
import { ObjectViewModalComponent } from "../../../components/modal-templates/object-view-modal/object-view-modal.component";
import { RciQualityReportModalComponent } from "../../../components/modal-templates/rci-quality-report-modal/rci-quality-report-modal.component";
import { ScriptValidationComponent, ScriptValidationItem } from "../../../components/modal-templates/script-validation/script-validation.component";
import { ValidateRecordedActivitiesComponent } from "../../../components/modal-templates/validate-recorded-activities/validate-recorded-activities.component";
import { ValidationWarningModalComponent } from "../../../components/modal-templates/validation-warning-modal/validation-warning-modal.component";
import { HighImpactDetailsComponent } from "../../../components/common/high-impact-details/high-impact-details.component";
import { SharedComponents } from "../../../components/shared.module";
import { TableWithHeaderComponent } from "../../../components/table-with-header/table-with-header.component";
import { KnowledgeGraphComponent } from "../../../components/test-case-generator/knowledge-graph/knowledge-graph.component";
import { tableTestAgentConfig } from "../../../core/table-templates/agent-browser-dropdown";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";
import { TestCaseDetailModalComponent } from "../../../components/modal-templates/test-case-detail-modal/test-case-detail-modal.component";
import { DataDisplayModalComponent } from "../../../components/modal-templates/data-display-modal/data-display-modal.component";
import { selectConnections } from "../../../core/ngrx/selectors/connection.selectors";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";

@Component({
  selector: "app-workspace-agentic-ai",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AgenticChatComponent,
    ExpandTableComponentComponent,
    KnowledgeGraphComponent,
    ...SharedComponents,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MultiTabTableComponent,
    EditableTableComponent,
    UiObjectTableComponent,
    ValidateRecordedActivitiesComponent,
    TableWithHeaderComponent,
    ExecutionCycleStatusCardComponent,
    ExpandableAccordionComponent,
    HighImpactDetailsComponent,
    NzTabsModule,
    NzSelectModule
  ],
  templateUrl: "./workspace-agentic-ai.component.html",
  styleUrl: "./workspace-agentic-ai.component.scss",
})
export class WorkspaceAgenticAIComponent extends BaseLayoutComponent {
  @ViewChild(AgenticChatComponent) chatComponent!: AgenticChatComponent;
  @ViewChild(ExpandTableComponentComponent)
  tableComponent!: ExpandTableComponentComponent;
  @ViewChild(MultiTabTableComponent) multiTabComponent!: MultiTabTableComponent;

  // Chat state
  response: any;
  message: any = "";
  messages: any = [];
  chatHistory: any;
  projectId: string = "";
  projectName: string = "";
  selectedSection: string = "Test Suites";
  threadId: any = "";
  isSumActive: boolean = false;
  isActive: boolean = false;
  isOrgChartActive: boolean = false;
  versionId: string = "";
  isProgressTracking: boolean = false;
  disableInput = false;
  progress: any = {
    percentage: 80,
    text: "",
  };
  processLogs: Array<{
    step: number;
    thinking?: string;
    evaluation?: string;
  }> = [];
  isProcessLogsExpanded = true;
  /** True while mobile/web script generation is tracking (enables thinking log UI). */
  isScriptProcessLogging = false;
  /** True once API returns logs as an array (including empty []); false while undefined. */
  processLogsDefined = false;
  viewId: string = '';

  uiObjects: any = [];
  selectedApplicationId: any = "";
  nextPrompt: string = "";
  activeGroupIndex = 0;
  selectedTrsRelease: string = 'All_Release';
  tableOptions: any = {
    pageSize: 10,
  };

  defaultMessageMap: any = {
    row_select: "Generate test cases for the listed rows",
    "tdg_test-case-select": "Generate test data for the listed rows",
  };

  // Sort options
  sortOptions: any[] = [
    { id: "test-case-count", label: "Test Case Count", direction: "asc" },
  ];

  private modalService = inject(NgbModal);
  private configService = inject(ConfigService);
  selectedTabTDG: any;
  selectedRowsTDG: any = [];
  isSaveLoading: boolean = false;
  automationProjectId: any;
  isLoading: boolean = false;
  agentMap: Record<string, string> = {};
  deviceMap: Record<string, string> = {};
  agentsLoaded = false;
  stopPolling$ = new Subject<void>();
  destroy$ = new Subject<void>();

  executionEngineOptions: any[] = [];
  executionEngineTableOptions: any[] = [];

  browserOptions: any[] = [
    {
      label: "Chrome",
      value: "Chrome",
      image: "assets/images/virtusa-icons/chrome.svg",
    },
    {
      label: "Firefox",
      value: "Firefox",
      image: "assets/images/virtusa-icons/firefox.svg",
    },
    {
      label: "Edge",
      value: "Edge",
      image: "assets/images/virtusa-icons/edge.svg",
    },
  ];

  selectedExecutionPlans: AccordionSection[] = [];
  testExecutionIsDefault = false;
  testExecutionEngine: any = null;
  testExecutionBrowser: any = null;
  isTestExecutionRowSelectAll = false;
  selectedTcCoverages: any = [];
  highImpactSaving = false;

  /**
   * Generate pagination info object with custom label
   * @param label - Label for pagination display (e.g., 'pplications', 'suites')
   * @param total - Total count (defaults to 100 for test cases, 1 for applications)
   * @returns Pagination info object
   */
  public getPaginationInfo(label: string, total: number = 100): any {
    return {
      currentStart: 1,
      currentEnd: 10,
      total: total,
      label: label,
    };
  }

  /**
   * Get accordion configuration based on item type/id
   * @param itemId - The id of the item to determine configuration for
   * @returns Configuration object with label, pagination info, and other settings
   */
  getAccordionConfig(itemId: string): any {
    console.log("Test Id-->>>", itemId);
    switch (itemId) {
      case "application_list":
      case "application_in_detail":
        return {
          itemLabel: "Page(s)",
          paginationInfo: this.getPaginationInfo("applications", 1),
          autoExpandFirst: itemId === "application_in_detail",
        };

      case "user_story_table":
        return {
          itemLabel: "User Stories",
          paginationInfo: this.getPaginationInfo("suites"),
          autoExpandFirst: false,
        };
      case "script_group_list":
        return {
          itemLabel: "Script(s)",
          paginationInfo: this.getPaginationInfo("test suites", 1),
          autoExpandFirst: false,
        };
      case "reusable_script_group_list":
        return {
          itemLabel: "Script(s)",
          paginationInfo: this.getPaginationInfo("test suites", 1),
          autoExpandFirst: false,
        };

      case "mappings_accordion":
         return {
          itemLabel: "",
          paginationInfo: this.getPaginationInfo("", 1),
          autoExpandFirst: false,
        };

      case "accordion_table":
      case "test_suites_accordion":
      case "custom_test_suites_accordion":
      default:
        return {
          itemLabel: "Test Case(s)",
          paginationInfo: this.getPaginationInfo("suites"),
          autoExpandFirst: false,
        };
    }
  }

  ngOnInit() {
    // Get project name from route params
    this.route.paramMap.subscribe((params) => {
      this.projectName = params.get("name") || "";
    });

    this.message = this.agenticAiService.getMessage();
    if (this.message !== "") {
      setTimeout(() => {
        this.chatComponent.send();
      }, 1000);
    }
    this.agenticAiService.setMessage("");
    this.chatHistory = this.agenticAiService.getHistory();
    this.messages = this.chatHistory?.messages || [];
    this.threadId = this.chatHistory?.threadId || "";
    this.agenticAiService.setHistory(null);

    // Get projectId from ProjectService
    this.projectService.getProjectId().subscribe((id: string | null) => {
      this.projectId = id || "";
      if (this.projectId) {
        this.loadExecutionEngines();
        this.restService.getAgents(this.projectId).subscribe({
          next: (res: any) => {
            (res?.data ?? []).forEach((agent: any) => {
              this.agentMap[agent.id] = agent.name;
            });
            this.agentsLoaded = true;
          },
          error: () => { },
        });
      }
    });

    this.projectService
      .getAutomationProjectId()
      .subscribe((id: string | null) => {
        this.automationProjectId = id || "";
        console.log("Automation Project ID in Agentic AI Workspace:", id);
      });

    // TODO: remove – dummy data to preview the high_impact_details component
    // this.response = {
    //   message: 'Impacted test cases identified',
    //   result: [
    //     {
    //       type: 'high_impact_details',
    //       id: 'high_impact_details',
    //       class: 'w-100',
    //       data: {
    //         impactedTestCases: [
    //           {
    //             testCaseID: 'TC_US2_POS_04',
    //             testScenario: 'Attempt to register a customer with a duplicate email address',
    //             methodName: 'getAllCustomers',
    //             sourceCodePath: 'src/main/avacomaflexbanking/services/PasswordService.java'
    //           },
    //           {
    //             testCaseID: 'TC_US2_POS_05',
    //             testScenario: 'Register new customer with valid unique email and phone number',
    //             methodName: 'createCustomer',
    //             sourceCodePath: 'src/main/avacomaflexbanking/services/CustomerService.java'
    //           },
    //           {
    //             testCaseID: 'TC_US2_NEG_01',
    //             testScenario: 'Attempt to register customer with invalid email format',
    //             methodName: 'validateEmail',
    //             sourceCodePath: 'src/main/avacomaflexbanking/validation/EmailValidator.java'
    //           },
    //           {
    //             testCaseID: 'TC_US2_NEG_02',
    //             testScenario: 'Attempt to register customer missing mandatory fields',
    //             methodName: 'validateCustomerData',
    //             sourceCodePath: 'src/main/avacomaflexbanking/validation/CustomerValidator.java'
    //           },
    //           {
    //             testCaseID: 'TC_US2_NEG_03',
    //             testScenario: 'Verify error when customer age is below minimum limit',
    //             methodName: 'checkCustomerAgeLimit',
    //             sourceCodePath: 'src/main/avacomaflexbanking/validation/CustomerValidator.java'
    //           },
    //           {
    //             testCaseID: 'TC_US2_NEG_04',
    //             testScenario: 'Ensure rejection if customer email format is invalid',
    //             methodName: 'validateEmailFormat',
    //             sourceCodePath: 'src/main/avacomaflexbanking/validation/CustomerValidator.java'
    //           }
    //         ],
    //         modifiedFiles: [
    //           {
    //             codeSnippetId: 'CS_001',
    //             sourceCodePath: 'src/main/avacomaflexbanking/services/CustomerService.java',
    //             recommendations: [
    //               { id: 'R1', description: 'Add null check before processing customer object' },
    //               { id: 'R2', description: 'Validate email uniqueness at service layer before DB call' }
    //             ]
    //           },
    //           {
    //             codeSnippetId: 'CS_002',
    //             sourceCodePath: 'src/main/avacomaflexbanking/services/InventoryManagement.java',
    //             recommendations: [
    //               { id: 'R3', description: 'Handle concurrent modification exception in inventory updates' }
    //             ]
    //           },
    //           {
    //             codeSnippetId: 'CS_003',
    //             sourceCodePath: 'src/main/avacomaflexbanking/authentication/UserAuthentication.java',
    //             recommendations: [
    //               { id: 'R4', description: 'Enforce password complexity policy on registration' },
    //               { id: 'R5', description: 'Rate-limit authentication attempts to prevent brute force' },
    //               { id: 'R6', description: 'Log failed login attempts with timestamp and IP address' }
    //             ]
    //           }
    //         ]
    //       }
    //     }
    //   ]
    // };
  }

  /**
   * Generic progress tracking method that polls an API endpoint
   * @param config Configuration object with API call and data extraction functions
   */
  iterateProgress(config: {
    apiCall: () => any;
    extractStatus: (response: any) => string;
    extractProgress: (response: any) => { text: string; percentage: number };
    onComplete?: () => void;
    onError?: (error: any) => void;
    onFailure?: (response: any) => void;
  }) {
    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.text = "Starting..";
    this.disableInput = true;

    interval(5000)
      .pipe(
        switchMap(() => config.apiCall()),
        takeWhile((statusResponse: any) => {
          const status = config.extractStatus(statusResponse);
          const isComplete =
            status === "COMPLETED" ||
            status === "FAILURE" ||
            status === "FAILED" ||
            status === "CANCELLED";

          // Extract progress data from response
          if (statusResponse?.data) {
            // Only update percentage while still in progress.
            // When COMPLETED, BE may return 0/0 paths which would flash the bar back to 0.
            if (!isComplete) {
              const progressData = config.extractProgress(statusResponse);
              this.progress.text = progressData.text;
              this.progress.percentage = progressData.percentage;
            }

            // Stop polling when generation is complete
            if (isComplete) {
              console.log(`Test case generation ${status.toLowerCase()}`);
              if (status === "COMPLETED") {
                if (this.nextPrompt) {
                  this.message = this.nextPrompt;
                  this.chatComponent.sendInput = this.nextPrompt;
                  this.chatComponent.send();
                }
                this.progress.percentage = 100;
                // Delay to show 100% completion

                setTimeout(() => {
                  console.log("Resetting progress and input state after completion");
                  this.chatComponent.sendInput = "";
                  this.progress.percentage = 0;
                  this.progress.text = "";
                  this.message = "";
                  this.isProgressTracking = false;
                  this.response = {};
                  this.isLoading = true;
                  this.disableInput = false;
                  this.nextPrompt = "";
                  if (config.onComplete) {
                    config.onComplete();
                  }
                }, 100);
              } else if (status == "FAILED") {
                this.progress.isError = true;
                this.disableInput = false;
                this.nextPrompt = "";
                if (config.onFailure) {
                  config.onFailure(statusResponse);
                }
              }
            }
          }

          // Return true to continue polling, false to stop
          return !isComplete;
        }, true),
      )
      .subscribe({
        error: (error) => {
          console.error("Error tracking progress:", error);
          this.isProgressTracking = false;
          this.progress.isError = true;
          this.disableInput = false;
          if (config.onError) {
            config.onError(error);
          }
        },
      });
  }

  /**
   * Track test case generation progress
   */
  trackTCGProgress() {
    if (!this.versionId || !this.projectId) {
      console.warn("Missing versionId or projectId for TCG progress tracking");
      return;
    }

    this.iterateProgress({
      apiCall: () =>
        this.restService.getTestCaseByVersionId(this.projectId, this.versionId),
      extractStatus: (response) => response?.data?.status,
      extractProgress: (response) => {
        const { message, totalPaths, processingPaths } = response.data;
        const randomIncrement = Math.floor(Math.random() * 6) + 5;
        return {
          text: message || "",
          percentage:Math.min(this.progress.percentage + randomIncrement, 100)
        };
      },
      onError: (error) => {
        console.error("Error tracking TCG progress:", error);
        const errorMessage =
          error?.error?.message ||
          error?.message ||
          MessageEnum.TEST_CASE_GENERATION_FAILURE;
        this.progress.text = errorMessage;
        this.store.dispatch(
          TestCaseGeneratorActions.generateTestCasesProgressMessages({
            message: { message: errorMessage, isError: true },
          })
        );
        this.toaster.pop({
          type: "error",
          text: errorMessage,
        });
      },
      onFailure: (response) => {
        const failureMessage =
          response?.data?.error ||
          MessageEnum.TEST_CASE_GENERATION_FAILURE;
        console.error("TCG generation failed:", failureMessage);
        this.progress.text = failureMessage;
        this.store.dispatch(
          TestCaseGeneratorActions.generateTestCasesProgressMessages({
            message: { message: failureMessage, isError: true },
          })
        );
        this.toaster.pop({
          type: "error",
          text: failureMessage,
        });
      },
    });
  }

  /**
   * Track user story extraction progress
   */
  trackUserStoryProgress() {
    if (!this.versionId || !this.projectId) {
      console.warn(
        "Missing versionId or projectId for user story progress tracking",
      );
      return;
    }

    this.iterateProgress({
      apiCall: () =>
        this.restService.getUserStoryProgress(this.projectId, this.versionId),
      extractStatus: (response) => response?.data?.progress?.status,
      extractProgress: (response) => ({
        text: response?.data?.progress?.tcgKgMessage || "",
        percentage: Math.round(response.data.completionPercentage),
      }),
      onError: (error) => {
        console.error("Error tracking user story progress:", error);
        const errorMessage = error?.error?.message || error?.message || MessageEnum.USER_STORY_PROGRESS_TRACKING_FAILURE;
        this.progress.text = errorMessage;
        this.store.dispatch(
          TestCaseGeneratorActions.generateTestCasesProgressMessages({
            message: { message: errorMessage, isError: true },
          })
        );
        this.toaster.pop({
          type: "error",
          text: errorMessage,
        });
      },
      onFailure: (response) => {
        const failureMessage = response?.data?.progress?.tcgKgMessage || response?.data?.message || MessageEnum.USER_STORIES_EXTRACTION_FAILURE;
        console.error("User story extraction failed:", failureMessage);
        this.progress.text = failureMessage;
        this.store.dispatch(
          TestCaseGeneratorActions.generateTestCasesProgressMessages({
            message: { message: failureMessage, isError: true },
          })
        );
        this.toaster.pop({
          type: "error",
          text: failureMessage,
        });
      },
    });
  }

  trackHighImpactTestCaseProgress() {
    if (!this.versionId || !this.projectId) {
      console.warn(
        "Missing versionId or projectId for optimized core test case progress tracking",
      );
      return;
    }

    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.text = "Starting optimized core test case generation...";
    this.disableInput = true;

    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getHighImpactTestCaseProgress(this.projectId, this.versionId)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error("An error occurred while tracking optimized core test case progress:", error);
          this.isProgressTracking = false;
          this.disableInput = false;
          return EMPTY;
        }),
      )
      .subscribe((response: any) => {
        const status = response?.data?.status;

        if (status === "FAILED" || status === "FAILURE") {
          this.stopPolling$.next();
          this.isProgressTracking = false;
          this.disableInput = false;
          this.progress.isError = true;
        } else if (status === "COMPLETED") {
          this.progress.text = "Optimized core test case generation completed!";
          this.progress.percentage = 100;
          this.disableInput = false;

          this.stopPolling$.next();

          // Send next prompt after completion
          setTimeout(() => {
            this.isProgressTracking = false;
            if (this.nextPrompt) {
              this.message = this.nextPrompt;
              this.chatComponent.sendInput = this.nextPrompt;
              this.chatComponent.send();
              this.nextPrompt = "";
            }
          }, 2000);
        } else {
          // Processing - add random progress
          this.progress.text = response?.data?.progress?.tcgKgMessage || "Generating optimized core test cases...";
          // Increment by random value between 5-10%, but cap at 90%
          const randomIncrement = Math.floor(Math.random() * 6) + 5;
          this.progress.percentage = Math.min(
            this.progress.percentage + randomIncrement,
            90,
          );
        }
      });
  }

  /**
   * Track application context upload progress
   */
  trackApplicationContextProgress() {
    if (!this.projectId) {
      console.warn(
        "Missing projectId for application context progress tracking",
      );
      return;
    }

    this.iterateProgress({
      apiCall: () =>
        this.restService.handleAsyncApplicationContext(this.projectId),
      extractStatus: (response) => response?.data?.status,
      extractProgress: (response) => ({
        text: response?.data?.message || "Processing application context...",
        percentage: response?.data?.progress || 0,
      }),
    });
  }

  getTableMeta(id: string) {
    if (id === "reclaim_execution_engine_list") {
      return {
        table: {
          options: {
            showPageSizeChanger: false,
            pageSize: 10,
            showPagination: true,
          },
          borderColor: "#02F576",
          hideVerticalBorder: true,
          hideTopBottomLines: true,
          whiteHeaderBg: true,
        },
        paginationPrefix: "Showing engines",
      };
    } else if (id === "execution_engine_list") {
      return {
        table: {
          options: {
            showPageSizeChanger: false,
            pageSize: 10,
            showPagination: true,
          },
          borderColor: "#02F576",
          hideVerticalBorder: true,
          hideHeaders: true,
          hideTopBottomLines: true,
        },
        paginationPrefix: "Showing engines",
      };
    } else if (id === "device_list") {
      return {
        table: {
          options: {
            showPageSizeChanger: false,
            pageSize: 10,
            showPagination: true,
          },
          borderColor: "#02F576",
          hideVerticalBorder: true,
          hideHeaders: true,
          hideTopBottomLines: true,
        },
        paginationPrefix: "Showing devices",
      };
    } else if (id === "code-mapping-coverage-table") {
      return {
        table: {
          options: {
            showPageSizeChanger: false,
            pageSize: 10,
            showPagination: true,
          },
          borderColor: "#02F576",
          hideVerticalBorder: true,
          hideHeaders: true,
          hideTopBottomLines: true,
        },
        paginationPrefix: "Showing mappings",
      };
    } else {
      return undefined;
    }
  }

  onViewResults(cycleId: string): void {
    this.sideNav.setTreeNode('testExecutionResults');
  }

  onTHComponentOutput(event: any, id: string) {
    if (id === 'source_code_mapping_table' && event?.type === 'rowSelect') {
      this.selectedTcCoverages = JSON.parse(JSON.stringify(event.sortedOutData));
      // Remove unwanted accessibility property
      this.selectedTcCoverages.forEach((ele: any) => {
        delete ele.select;
      });

    } else if (
      id === "execution_engine_list" &&
      event?.data?.columnConfig?.actionKey === "setupEngine"
    ) {
      this.setupAgentModal(event.data?.row?.apiKey);
    } else if (event?.actionKey === "reclaimEngine") {
      console.log("reclaiming");
    } else if (
      id === "device_list" &&
      event?.data?.columnConfig?.actionKey === "deviceInfo"
    ) {
      this.openDeviceInfoModal(event.data?.row);
    } else if (event?.type === "link") {
      this.testCaseDetailModal(event?.data?.row);
    }
  }

  saveTestCoverage(mappingId: string): void {
    if (this.selectedTcCoverages.length === 0) {
      this.toaster.pop({
        type: "warning",
        text: MessageEnum.NO_DATA_SELECTED,
      });
      return;
    }
    this.highImpactSaving = true;
    this.restService.approveMapping({
      id: mappingId,
      data: this.selectedTcCoverages
    }).subscribe({
      next: (response: boolean) => {
        if (response) {
          this.toaster.pop({
            type: "success",
            text: MessageEnum.MAPPING_SAVED_SUCCESSFULLY,
          });
        }
        this.highImpactSaving = false;
      },
      error: (error: any) => {
        console.error(error);
        this.highImpactSaving = false;
        this.toaster.pop({
          type: "error",
          text: error?.message || MessageEnum.SOMETHING_WENT_WRONG,
        });
      }
    });
  }

  setupAgentModal(key: string) {
    const modalRef = this.modalService.open(AgentSetupInfoComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    modalRef.componentInstance.key = key;
    modalRef.componentInstance.projectId = this.projectId;
    modalRef.result.then((result) => {
      console.log(result);
    });
  }

  openDeviceInfoModal(device: any): void {
    const modalRef = this.modalService.open(DeviceInfoModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
    });
    modalRef.componentInstance.device = device;
  }

  /**
   * Cancel test case generation for the current version
   * Sends DELETE request to: /backend/api/v1/tcg/cancelTestcases/{projectId}/{versionId}
   * Handles cancellation response and updates progress state
   *
   * Response format:
   * {
   *   "status": 200,
   *   "message": "Success",
   *   "data": {
   *     "message": "Test case generation cancelled successfully",
   *     "versionId": "...",
   *     "processesKilled": 1
   *   }
   * }
   */
  cancelTCG(): void {
    if (!this.versionId || !this.projectId) {
      console.warn(
        "Missing versionId or projectId to cancel test case generation",
      );
      this.toaster.pop({
        type: "error",
        text: MessageEnum.CANNOT_CANCEL_MISSING_VERSION_OR_PROJECT_ID,
      });
      return;
    }

    // Call the cancel API
    this.restService
      .cancelTestcaseGeneration(this.projectId, this.versionId)
      .subscribe({
        next: (response: any) => {
          this.disableInput = false;
          if (response?.status === 200 && response?.data) {
            this.messages[
              this.messages.length - 1
            ].tool_data.result[0].data.isCancelled = true;
            // Update progress state
            this.progress.text =
              response.data?.message || "Test case generation cancelled";
            this.progress.percentage = 0;
            setTimeout(() => {
              this.isProgressTracking = false;
            }, 3000);

            // Show success toast
            this.toaster.pop({
              type: "success",
              text:
                response.data?.message ||
                MessageEnum.TEST_CASE_GENERATION_CANCELLED,
            });

            // Optionally, refresh the progress status one more time to confirm cancellation
            // This will fetch the latest status showing "CANCELLED"
          } else {
            throw new Error("Invalid response from cancel API");
          }
        },
        error: (error: any) => {
          console.error("Error cancelling test case generation:", error);
          this.isProgressTracking = false;

          const errorMessage =
            error?.error?.message ||
            error?.message ||
            MessageEnum.TEST_CASE_GENERATION_CANCEL_FAILURE;

          this.toaster.pop({
            type: "error",
            text: errorMessage,
          });
        },
      });
  }

  onChatEmit(event: any) {
    if (event?.result[0]?.id) this.viewId = event?.result[0]?.id;
    console.log("SAVED DATA RESPONSE ====>", event);
    this.isLoading = false;

    // Signals emitted when the AI stream ends with no tool data (plain text / error)
    if (event.type === "stream_completed" || event.type === "stream_error") {
      return;
    }

    if (event?.id === 'execution_engine_list') {
      this.loadExecutionEngines();
    }

    // Deep-clone the event so that all subsequent transformations (grouping scripts into
    // sections, mapping plan items, annotating testcaseCount, setting formattedData, etc.)
    // operate on a fresh copy and never mutate the original tool_data stored in the chat
    // message. Without this, re-clicking "Preview" on the same chat message would feed
    // already-transformed data back through the processing pipeline, producing wrong output
    // on the right-hand panel (e.g. groupTestScriptsToSections running on already-grouped
    // sections, or testPlanId being undefined because sections now use "id" instead).
    event = structuredClone(event);

    let result = event.result[0];

    if (event.result[0]?.nextPrompt) {
      this.nextPrompt = event.result[0]?.nextPrompt;
    }

    if (
      result?.type === "script_execution_started" &&
      result?.id === "script_execution_started"
    ) {
      window.open(result?.data[0]?.configurations?.url, "_blank");
    }

    if (event.result[0]?.id === "execution_results") {
      const relem = this.utilityService?.extractRealmFromUrl() as string;
      const reportUrl = this.configService.getConfig("reportUrl");
      const tenantId = encodeURIComponent(relem);
      const automationId = result?.data[0]?.automationProjectId;
      const reportId = result?.data[0]?.id;
      const url = `${reportUrl}?tenantId=${tenantId}&projectId=${this.projectId}&automationProjectId=${automationId}&reportId=${reportId}&type=${"SCRIPT"}`;
      window.open(url, "_blank");
    }

    if (
      event.result[0]?.type === "open_test_script_in_editor_view" &&
      event.result[0]?.id === "open_test_script_in_editor_view"
    ) {
      this.openEditorView({ scriptId: event.result[0].data?.id ? event.result[0].data.id : event.result[0].data?.scriptId });
    }

    if (event.result[0]?.id === "test_case_progress") {
      this.versionId = event.result[0]?.data?.versionId;
      // Start progress tracking when test case generation starts
      if (this.versionId && this.projectId) {
        this.trackTCGProgress();
      }
    }

    if (event.result[0]?.id === "high_impact_test_case_progress") {
      this.versionId = event.result[0]?.data?.versionId;
      // Start progress tracking for prirotized test case extraction
      if (this.versionId && this.projectId) {
        this.trackHighImpactTestCaseProgress();
      }
    }

    if (event.result[0]?.id === "application_context_upload_progress") {
      const fileName = event.result[0]?.data?.fileName;
      // Start progress tracking for application context upload
      if (fileName && this.projectId) {
        this.trackApplicationContextProgress();
      }
    }

    if (event.result[0]?.id === "user_story_extraction_progress") {
      this.versionId = event.result[0]?.data?.configId;
      // Start progress tracking for user story extraction
      if (this.versionId && this.projectId) {
        this.trackUserStoryProgress();
      }
    }

    if (event.result[0]?.id === "tdg_progress") {
      let taskId = event.result[0]?.task_id;
      // Start progress tracking for test data generation
      if (taskId) {
        this.trackTestDataGenerationProgress(taskId);
      }
    }

    if (event.result[0]?.id === "web_script_recorder_progres") {
      let id = event.result[0]?.data?.recordingId;
      // Start progress tracking for recorded activities
      if (id) {
        this.loadAIRecordActivities(id);
      }
    }

    if (event.result[0]?.id === "mobile_script_recorder_progres") {
      let id = event.result[0]?.data?.recordingId;
      // Start progress tracking for recorded activities
      if (id) {
        this.loadMobileScriptActivities(id);
      }
    }

    if (event.result[0]?.id === "api_script_recorder_progres") {
      let id = event.result[0]?.data?.recordingId;
      // Start progress tracking for recorded activities
      if (id) {
        this.trackApiScriptProgress(id);
      }
    }

    if (event.result[0]?.id === "high_impact_mapping_progress") {
      this.versionId = event.result[0].asyncToken;
      let mappingId = event.result[0].mappingId;
      // Start progress tracking for mapping create - high impact
      if (this.versionId && this.projectId) {
        this.trackMappingProgress(mappingId);
      }
    }

    if (event.result[0]?.id === "recommendations_progress") {
      this.versionId = event.result[0].asyncToken;
      // Start progress tracking for recommendations - high impact
      if (this.versionId && this.projectId) {
        this.trackMappingProgress('', true);
      }
    }

    if (event.result[0]?.id === "impacted_testcases_progress") {
      this.versionId = event.result[0].mappingId;
      // Start progress tracking for impacted test cases - high impact
      if (this.versionId && this.projectId) {
        this.trackImpactedProgress(this.versionId);
      }
    }

    if (event.result[0]?.id === "most_impacted_testcases_progress") {
      this.versionId = event.result[0]?.mappingId;
      let reportName = event.result[0]?.reportName;
      // Start progress tracking for most impacted test cases - high impact
      if (this.versionId) {
        this.trackMostImpactedProgress(this.versionId, reportName);
      }
    }

    // Store parseFileId in localStorage if present
    if (event.result[0]?.data?.parseFileId) {
      localStorage.setItem("parseFileId", event.result[0].data.parseFileId);
      console.log(
        "Stored parseFileId in localStorage:",
        event.result[0].data.parseFileId,
      );
    }

    if (event?.result[0]?.type === 'code_coverage_table' && event?.result[0]?.id === 'source_code_mapping_table') {
      event.result[0].columns = [
        {
          key: 'row_select',
          display_name: '',
          headerStyles: { 'width': 0 },
          _config: {
            type: 'checkbox',
            displayInColumn: true
          }
        },
        {
          key: 'testCase',
          display_name: 'Test Case',
          headerStyles: {
            background: '#1E2235',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600'
          },
          _config: {
            type: 'double-line-data',
            targetKey: {
              firstLine: 'testCaseID',
              secondLine: 'testScenario'
            }
          }
        },
        {
          key: 'method',
          display_name: 'Method & Source Code Path',
          headerStyles: {
            background: '#1E2235',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600'
          },
          _config: {
            type: 'double-line-data',
            style: {
              gap: '8px'
            },
            prefixImage: {
              isStatic: true,
              url: 'assets/images/virtusa-icons/code.png',
              style: {
                width: '16px',
                marginRight: '6px',
                marginLeft: '2px'
              }
            },
            suffixImage: {
              isStatic: true,
              url: 'assets/images/virtusa-icons/report-gray.png',
              style: {
                height: '14px',
                width: '12px',
                marginRight: '12px',
                marginLeft: '2px'
              }
            },
            targetKey: {
              firstLine: 'methodName',
              secondLine: 'sourceCodePath'
            }
          }
        },
        {
          key: 'action',
          display_name: 'Actions',
          headerStyles: {
            background: '#1E2235',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            width: '120px'
          },
          _config: {
            type: 'link',
            name: 'View Details',
            actionKey: 'mapping_view'
          }
        }
      ];
    }

    if (
      (event.result[0]?.type === "editableTable" ||
        event.result[0]?.type === "table") && event?.result[0]?.id !== 'source_code_mapping_table'
    ) {
      event.result[0].formattedData = {};
      event.result[0].formattedData.columns =
        event.result[0].data?.tableData?.columns?.map((col: string) => ({
          key: col,
          display_name: col,
          _editConfig: {
            type: "input",
          },
        }));
      event.result[0].formattedData.data = this.getTransformed(
        event.result[0].data?.tableData,
      );
    }

    if (event?.type === "clearRowSelection") {
      this.tableComponent?.onAllChecked(false);
      return;
    }

    // Handle response with result array
    // Structure: { message: "...", id: "...", result: [{ type: "...", id: "...", class: "...", data: {...} }, ...] }
    if (Array.isArray(event?.result)) {
      // Patch date column configs for known table types
      const DATE_KEYS = new Set([
        "startDate",
        "releaseDate",
        "endDate",
        "start_date",
        "release_date",
        "end_date",
      ]);
      event.result.forEach((resultItem: any) => {
        if (
          resultItem?.id === "release_list" &&
          Array.isArray(resultItem?.columns)
        ) {
          resultItem.columns = resultItem.columns.map((col: any) =>
            DATE_KEYS.has(col.key)
              ? { ...col, _config: { type: "date", format: "MMMM dd, yyyy" } }
              : col,
          );
        }

        // Load agentMap and transform device_list display values to sentence case.
        if (resultItem?.id === 'device_list' && Array.isArray(resultItem?.data)) {
          const toSentenceCase = (s: string) =>
            s ? s.replace(/_/g, ' ').charAt(0).toUpperCase() + s.replace(/_/g, ' ').slice(1).toLowerCase() : s;
          const applyTransform = () => {
            resultItem.data = resultItem.data.map((row: any) => ({
              ...row,
              deviceType: toSentenceCase(row.deviceType),
              environment: toSentenceCase(row.environment),
              remoteEnvironment: row.remoteEnvironment ? toSentenceCase(row.remoteEnvironment) : row.remoteEnvironment,
              agentName: (row.agentId && this.agentMap[row.agentId]) ? this.agentMap[row.agentId] : (row.agentId === '' ? 'On Demand' : '-'),
            }));
          };
          if (!this.agentsLoaded && resultItem.data.some((r: any) => r.agentId)) {
            this.restService.getAgents(this.projectId).subscribe({
              next: (agents) => {
                (agents?.data ?? []).forEach((agent: any) => { this.agentMap[agent.id] = agent.name; });
                this.agentsLoaded = true;
                applyTransform();
              },
              error: () => applyTransform(),
            });
          } else {
            applyTransform();
          }
        }

        // Group flat test-script list into accordion sections by scriptGroupName.
        // engineSections is intentionally left out here; it is injected on expand
        // so the async loadExecutionEngines() race is handled gracefully.
        if (
          resultItem?.id === 'execution_test_script_list_accordion' &&
          Array.isArray(resultItem?.data)
        ) {
          resultItem.data = this.groupTestScriptsToSections(resultItem.data);
        }

        // Transform execution_test_plan_list_accordion raw items into AccordionSection shape,
        // pre-populating tableColumns and tableRows from the embedded testScripts array.
        // engineSections is left empty here and injected lazily on first expand so the
        // async loadExecutionEngines() race is handled gracefully (same as the script accordion).
        if (
          resultItem?.id === 'execution_test_plan_list_accordion' &&
          Array.isArray(resultItem?.data)
        ) {
          const columns = this.getExecutionTestPlanListAccordionColumns();
          resultItem.data = resultItem.data.map((item: any) => ({
            id: item.testPlanId,
            title: item.testPlanName,
            name: item.testPlanName,
            testPlanId: item.testPlanId,
            testPlanName: item.testPlanName,
            createdAt: item.createdAt,
            testcaseCount: (item.testScripts ?? []).length,
            itemCount: (item.testScripts ?? []).length,
            tableColumns: columns,
            tableRows: (item.testScripts ?? []).map((row: any) => ({
              ...row,
              // Default to 'On Demand' agent when no agent is assigned or empty string
              agentId: row.agentId || 'onDemand',
              // Build version dropdown options: prefer the versions array, fall back to scalar version string
              versionOptions: row.versions?.length
                ? row.versions.map((v: any) => ({ label: v.version, value: v.version }))
                : row.version ? [{ label: row.version, value: row.version }] : [],
              // engineSections deliberately empty — filled on first expand
              engineSections: [],
              browserOptions: this.browserOptions.map(o => o.value),
            })),
            loading: false,
            expanded: false,
          }));
        }

        // Annotate each section item with testcaseCount so the accordion header
        // can display the number of embedded test scripts.
        if (
          resultItem?.id === 'selected_test_plans_and_scripts_accordion' &&
          Array.isArray(resultItem?.data)
        ) {
          resultItem.data = resultItem.data.map((item: any) => ({
            ...item,
            testcaseCount: (item.testScripts ?? []).length,
          }));
        }

        // Set testCoverageConfigs API params for the test case table so the
        // Test Coverage modal can call the real analysis and results endpoints.
        if (resultItem?.id === 'test_case_table') {
          resultItem.testCoverageConfigs = {
            apiParams: {
              project_id: this.projectId,
              version_id: event.versionId ?? '',
              config_id: event.configId ?? '',
            },
          };
        }
      });

      // Sync release dropdown selection from TRS result items
      const trsItem = event.result.find((r: any) =>
        r.type === 'trs_all_metrics_data' || r.type === 'trs_single_metric_data'
      );
      if (trsItem?.data?.selectedReleaseId) {
        this.selectedTrsRelease = trsItem.data.selectedReleaseId;
      } else if (trsItem) {
        this.selectedTrsRelease = 'All_Release';
      }

      let popupInd = event.result?.findIndex((ele: any) => ele.isPopup);
      if (popupInd >= 0) {
        this.handlePopup(
          event.result[popupInd].type,
          event.result[popupInd].data,
        );
      } else {
        this.response = event;
      }
    }
  }

  trackTestDataGenerationProgress(taskId: any) {
    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.text = "Starting..";
    this.disableInput = true;

    this.restService
      .pollTdgTaskStatus(this.tdgService.getProjectId(), taskId, 7000)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error("An error occurred while loading activities:", error);
          this.isProgressTracking = false;
          this.disableInput = false;
          return EMPTY;
        }),
      )
      .subscribe((res: any) => {
        if (res.status === "failed") {
          this.stopPolling$.next();
          this.isProgressTracking = false;
          this.disableInput = false;
        } else if (res.status === "processing") {
          this.progress.text = "Generating synthetic test data...";
          // Increment by random value between 5-10%, but cap at 95%
          const randomIncrement = Math.floor(Math.random() * 6) + 5;
          this.progress.percentage = Math.min(
            this.progress.percentage + randomIncrement,
            90,
          );
        } else if (res.status === "completed") {
          this.progress.text = "Data Generation Completed!";
          this.progress.percentage = 100;
          this.disableInput = false;

          this.stopPolling$.next();
          if (res.result?.parseFileId) {
            localStorage.setItem("parseFileId", res.result?.parseFileId);
          }
          // let parseFileId = res.result?.parseFileId;
          let parseFileId =
            res.result?.parseFileId ||
            localStorage.getItem("parseFileId") ||
            "";
          if (parseFileId) {
            this.syncCustomData(parseFileId, true);
          }

          //load synthetic data after 3 seconds
          setTimeout(() => {
            this.isProgressTracking = false;
            this.message = "Load the generated synthetic data";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();
          }, 2000);
        }
      });
  }

  handlePopup(popupType: string, data: any) {
    if (popupType === "quality_report_details") {
      let modalRef = this.modalService.open(RciQualityReportModalComponent, {
        centered: true,
        size: "xl",
        backdrop: "static",
      });

      modalRef.componentInstance.configs = {
        data,
        isDataReady: true,
      };
    } else if (popupType === "recording_completed_api") {
      this.openAPIScriptValidationModal(data?.length ? data[0] : {}, popupType);
    } else if (popupType === "recording_completed" || popupType === "recording_completed_mobile") {
      this.showScriptGenerationModal(data?.length ? data[0] : {}, popupType);
    }
  }

  openAPIScriptValidationModal(data: any, type: string) {
    const svModalRef = this.modalService.open(ScriptValidationComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'scrip-mapping-modal'
    });


    const responseData = data?.response || data?.responseAutomaticScriptGenerator || data;

    const svTreeNodes = [
      {
        id: 'schemas',
        displayName: 'Schemas',
        count: responseData.schemas.length,
        children: responseData.schemas.map((ele: any) => ({
          id: ele.schema.id,
          displayName: ele.schema.name
        }))
      },
      {
        id: 'services',
        displayName: 'Services',
        count: responseData.services.length,
        children: responseData.services.map((ele: any) => ({
          id: ele.id,
          displayName: ele.name
        }))
      }
    ];

    const nodeDataMap: { [nodeId: string]: ScriptValidationItem[] } = {};

    responseData.schemas.forEach((ele: any) => {
      nodeDataMap[ele.schema.id] = [{
        id: `${ele.schema.id}_attributes`,
        name: 'Attributes',
        validated: false,
        enabled: false,
        expanded: false,
        fields: (ele.schema.attributes || []).map((attr: any) => ({ name: attr.name, dataType: attr.dataType }))
      }];
    });

    responseData.services.forEach((service: any) => {
      nodeDataMap[service.id] = (service.operations || []).map((op: any) => ({
        id: op.id === 'uuid' ? `${service.id}_${op.name}` : op.id,
        name: op.name,
        validated: false,
        enabled: false,
        expanded: false,
        fields: op.parameters?.map((p: any) => ({ name: p.name, dataType: p.type })) || []
      }));
    });

    svModalRef.componentInstance.treeNodes = svTreeNodes;
    svModalRef.componentInstance.nodeDataMap = nodeDataMap;

    svModalRef.result.then(() => {
      this.chatComponent.sendInput = "Save the recorded script";
      this.chatComponent.send();
    }, () => { });
  }

  showScriptGenerationModal(data: any, type: string) {
    const isWeb = type === 'recording_completed';
    const scriptType = isWeb ? 'web' : 'mobile';

    // Extract the page list from the response payload — mirrors agenticDataFormat()
    // in ValidateRecordedActivitiesComponent:
    //   web  → data.responseAutomaticScriptGenerator.pages
    //   mobile → (data.response || data).pages
    const responseData = isWeb
      ? data?.responseAutomaticScriptGenerator
      : (data?.response || data);

    const pages: any[] = responseData?.pages || [];

    // Build ScriptValidationItem list — mirrors openWebScriptValidation() /
    // openMobileScriptValidation() in ValidateRecordedActivitiesComponent
    const allPageItems: ScriptValidationItem[] = pages.map((page: any) => ({
      id: page.id,
      name: page.name,
      url: page.url,
      validated: false,
      enabled: false,
      expanded: false,
      fields: isWeb
        ? (page.uiObjects || []).map((obj: any) => ({
            name: obj.name,
            locator: obj.locators?.[0]?.locator || '',
            type: obj.locators?.[0]?.type || ''
          }))
        : (page.mobileObjects || page.uiObjects || []).map((obj: any) => ({
            name: obj.name,
            dataType: obj.locators?.[0]?.locator || obj.locator || ''
          }))
    }));

    const svTreeNodes = [{
      id: 'pages',
      displayName: 'Pages',
      count: pages.length,
      children: pages.map((page: any) => ({ id: page.id, displayName: page.name }))
    }];

    const nodeDataMap: { [key: string]: ScriptValidationItem[] } = {};
    pages.forEach((page: any) => {
      nodeDataMap[page.id] = allPageItems;
    });

    const svModalRef = this.modalService.open(ScriptValidationComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'scrip-mapping-modal'
    });
    svModalRef.componentInstance.treeNodes = svTreeNodes;
    svModalRef.componentInstance.nodeDataMap = nodeDataMap;
    svModalRef.componentInstance.type = scriptType;

    svModalRef.result.then(() => {
      this.chatComponent.sendInput = 'Save the recorded script';
      this.chatComponent.send();
    }, () => {});
  }

  getInfoOptions(id: string) {
    let res = {
      display: false,
      tooltip: ''
    };
    if (id === 'mappings_accordion') {
      res = {
        display: true,
        tooltip: 'Show configs'
      };
    }
    return res;
  }

  onTableChange(event: any) {
    if (event.type === "row_select") {
      let rows = event.data.map((ele: any) => {
        return {
          ...ele,
          chipName: ele.testCaseId || ele.storyId,
          chipValue: ele.testCaseId || ele.storyId,
        };
      });
      this.chatComponent.selectedRows = rows;
      this.message = rows?.length
        ? this.getDefaultMesage(this.response?.result[0].id)
        : this.defaultMessageMap["tdg_test-case-select"];
      // this.message = rows?.length ? this.getDefaultMesage(this.response?.result[0].id) :this.defaultMessageMap[event.type];
    } else if (event.type === 'quality_report') {
      this.openReport(event.data);
    } else if (event.type === 'optimized_core_test_cases') {
      this.chatComponent.sendInput = 'Generate Optimized Core Test Cases';
      this.chatComponent.send();
    } else if (event.type === "thumbs_down") {
      this.message = "Provide feedback ";
      let row = {
        chipName: event?.data?.testCaseId,
        chipValue: event?.data?.testCaseId,
      };
      this.chatComponent.selectedRows = [row];
      this.chatComponent.sendInput = this.message;

      setTimeout(() => {
        this.chatComponent.send();
        this.tableComponent?.onAllChecked(false);
        this.message = "";
      }, 1000);
    } else if (event.type === "infoIcon") {
      // TO-DO: Implement info icon click handling based on the specific requirements (e.g., show tooltip, open modal with details, etc.)
    }
  }

  getDefaultMesage(type: string, selectedRowsLength?: number) {
    if (type === "user_story_table") {
      return " Generate test cases for the listed rows";
    } else if (type === "test_suites_accordion") {
      return " Generate synthetic test data for below test cases :";
    } else if (type === "custom_test_suites_accordion") {
      return " this is the selected test case list";
    } else if (type === "application_list") {
      return "Generate test scripts. Use application: ";
    } else if (type === 'mappings_accordion') {
      if(selectedRowsLength && selectedRowsLength > 1) {
        return "Delete Mappings:";
      }else{
        return "Generate impacted Test Cases for Mapping:";
      }
    } else {
      return "";
    }
  }

  openReport(event: any) {
    this.message =
      "View the Quality report for story key " +
      event?.storyId +
      " and report id " +
      event?.qualityReportId;

    this.chatComponent.selectedRows = [];
    this.chatComponent.sendInput = this.message;
    // Use a timeout to ensure the message input is updated in the child component before sending
    setTimeout(() => {
      this.chatComponent.send();
      this.tableComponent?.onAllChecked(false);
      this.message = "";
    }, 1000);
  }

  getDisplayMessage(): string {
    if (!this.response?.message) {
      return "";
    }
    // If message starts with "Error:", don't show anything
    if (this.response.message.startsWith("Error:")) {
      return "";
    }
    return this.response.message;
  }

  /**
   * Handle sort change event from accordion component
   * @param sortOption - The selected sort option
   */
  onSortChange(sortOption: any): void {
    console.log("Sort changed:", sortOption);
    // Sorting logic is now handled internally by accordion component
  }


  getItemData(item: any) {
    let res: any = item?.data;
    if (item.id === 'recommendations_table') {
      let formattedData = {
        rows: item.data,
        columns: [
          { mapping_key: "codeSnippetId", display_name: "Code Snippet Id", isVisible: true },
          { mapping_key: "sourceCodePath", display_name: "Source Code Path", isVisible: true }
        ]
      };
      res = formattedData;
    }
    return res;
  }

  getDescriptionImage(id: string) {
    if (id === 'recommendations_table') {
      return {
        prefix: {
          url: 'assets/images/virtusa-icons/report-gray.png'
        }
      }
    } else {
      return {
        prefix: {
          url: 'assets/images/virtusa-icons/document-icon.svg'
        }
      };
    }
  }

  getIsSelectionEnable(typeId: string) {
    return typeId === "mappings_accordion";
  }

  onInfoIconClick(event: any, typeId: string) {
    if (typeId === 'mappings_accordion') {
      this.openMappingConfigsModal(event?.data);
    }
  }

  getTableOptions(id: string) {
    if (id === 'mappings_accordion') {
      return {
        showPagination: true,
        pageSize: 10
      };
    } else {
      return {};
    }
  }

  isTcCountHidden(id: string) {
    if (id === 'mappings_accordion') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Handle section expand event - Make BE call to fetch data
   * @param section - The expanded section
   */
  onSectionExpand(event: { section: AccordionSection; typeId?: string }): void {
    const section = event.section;
    const typeId = event.typeId;
    console.log("Section expanded:", section, "Type ID:", typeId);
    // Set loading state
    section.loading = true;
    if (typeId === "test_suites_accordion") {
      section.tableColumns = [
        {
          display_name: "",
          key: "checkbox",
          showExpand: false,
          noBorderRight: true,
          headerStyles: {
            "margin-left": "5px",
          },
          _config: {
            type: "checkbox",
            displayInColumn: true,
          },
        },
        {
          display_name: "Test Case ID",
          key: "testCaseId",
        },
        {
          display_name: "Test Case Title",
          key: "testCase",
        },
      ];

      this.restService
        .getTestCaseByVersionId(this.projectId, section?.testSuiteId)
        .subscribe({
          next: (response) => {
            console.log("Fetched section data:", response?.data?.testCases);
            section.tableRows = response?.data?.testCases;
            section.loading = false;
          },
          error: (error) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });
    } else if (typeId === "custom_test_suites_accordion") {
      section.tableColumns = [
        { key: "testCaseId", display_name: "Test Case ID" },
        { key: "testCase", display_name: "Test Case" },
        { key: "testScenario", display_name: "Test Scenario" },
        { key: "updated_at", display_name: "Timestamp" },
        {
          key: "action",
          display_name: "",
          _config: {
            type: "link",
            displayInColumn: false,
            name: "Preview",
          },
        },
      ];

      this.restService
        .getCustomTablesByTestSuite(this.projectId, section?.testSuiteName)
        .subscribe({
          next: (response) => {
            console.log("Fetched section data:", response?.data);
            section.tableRows = response?.data;
            section.loading = false;
          },
          error: (error) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });
    } else if (
      typeId === "application_list" ||
      typeId === "application_in_detail"
    ) {
      section.tableColumns = [
        {
          key: "name",
          display_name: "Page Name",
          headerStyles: {
            width: "200px",
          },
        },
        {
          key: "url",
          display_name: "URL",
          cellStyles: {
            "max-width": "200px",
            "word-break": "break-all",
            "white-space": "normal",
          },
        },
        {
          key: "objectsCount",
          display_name: "Objects",
          headerStyles: {
            width: "200px",
          },
          _config: {
            type: "link",
            displayInColumn: true,
            name: "",
          },
        },
      ];

      this.selectedApplicationId = section?.id;
      this.restService
        .getAutomationPage(
          this.projectId,
          this.automationProjectId,
          String(section?.id),
        )
        .subscribe({
          next: (response) => {
            console.log("Fetched section data:", response?.data);
            // Add objectsCount to each row with formatted display
            const transformedData = response?.data?.map((row: any) => {
              const count = row.uiObjects?.length || 0;
              return {
                ...row,
                objectsCount: count === 1 ? "1 Object" : `${count} Objects`,
              };
            });
            section.tableRows = transformedData;
            section.loading = false;
          },
          error: (error) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });
    } else if (typeId === "tcg_test_suite_list") {
      section.tableColumns = [
        {
          display_name: "",
          key: "checkbox",
          showExpand: false,
          noBorderRight: true,
          headerStyles: {
            "margin-left": "5px",
          },
          _config: {
            type: "checkbox",
            displayInColumn: false,
            singleSelect: true,
          },
        },
        {
          display_name: "Test Case ID",
          key: "testCaseId",
        },
        { key: "testCase", display_name: "Test Case" },
        { key: "testScenario", display_name: "Test Scenario" },
      ];

      this.restService
        .getTestCaseByVersionId(this.projectId, section?.testSuiteId)
        .subscribe({
          next: (response) => {
            console.log("Fetched section data:", response?.data?.testCases);
            section.tableRows = response?.data?.testCases;
            section.loading = false;
          },
          error: (error) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });
    } else if (typeId === "script_group_list" || typeId === "reusable_script_group_list") {
      section.tableColumns = [
        {
          display_name: "",
          key: "checkbox",
          showExpand: false,
          hideForChildren: true,
          noBorderRight: true,
          headerStyles: {
            background: "#7593F2",
            width: "0px",
          },
          _config: {
            type: "checkbox",
            displayInColumn: false,
            singleSelect: true,
          },
        },
        {
          display_name: "Script Name",
          key: "name",
          hideForChildren: true,
          headerStyles: {
            background: "#7593F2",
            color: "#ffffff",
          },
        },
        {
          display_name: "Test Case Name",
          key: "tcgTestCaseResultName",
          headerStyles: {
            background: "#7593F2",
            color: "#ffffff",
          },
        },
        {
          key: "action",
          display_name: "",
          hideForChildren: true,
          _config: {
            type: "link",
            displayInColumn: false,
            name: "view script",
          },
        },
      ];
      this.restService
        .getAutomationTestScripts(this.projectId, this.automationProjectId)
        .subscribe({
          next: (response) => {
            let testScript;
            if (typeId === 'script_group_list') {
              testScript = response?.data?.filter(
                (item: any) => item?.type === "SCRIPT",
              );
            } else {
              testScript = response?.data?.filter(
                (item: any) => item?.type === "REUSABLE_SCRIPT",
              );
            }

            let filteredScripts: any = [];
            if (section.isDefault) {
              filteredScripts = (testScript ?? []).filter(
                (item: any) => !item?.automationScriptGroup
              );
            } else {
              filteredScripts = (testScript ?? []).filter(
                (item: any) => item?.automationScriptGroup?.id === section.id,
              );
            }
            section.tableRows = this.paramTableData(filteredScripts);
            console.log("Fetched section data:", section.tableRows);
            section.loading = false;
          },
          error: (error) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });
    } else if (
      typeId === "test_plan_list_accordion" ||
      typeId === "test_plan_detail_accordion"
    ) {
      const browserIconStyle = { width: "20px", height: "20px" };
      const browserValueMap: Record<string, { value: string; style: any }> = {
        chrome: {
          value: "assets/images/virtusa-icons/chrome.svg",
          style: browserIconStyle,
        },
        firefox: {
          value: "assets/images/virtusa-icons/firefox.svg",
          style: browserIconStyle,
        },
        edge: {
          value: "assets/images/virtusa-icons/edge.svg",
          style: browserIconStyle,
        },
      };

      section.tableColumns = [
        {
          key: "name",
          display_name: "Test Case Name",
          headerStyles: { width: "45%" },
        },
        {
          key: "version",
          display_name: "Version",
          headerStyles: { width: "15%" },
        },
        {
          key: "agentName",
          display_name: "Execution engine/Device",
          headerStyles: { width: "30%" },
        },
        {
          key: "browser",
          display_name: "Browser",
          headerStyles: { width: "10%" },
          _config: {
            type: "image",
            displayInColumn: false,
            isConditional: true,
            targetKey: "browserKey",
            value: browserValueMap,
          },
        },
      ];

      const mapRows = (response: any) =>
        (response?.data?.testScripts ?? []).map((row: any) => ({
          ...row,
          browserKey: row.browser?.toLowerCase?.() ?? "",
          agentName: (row.agentId && row.agentId !== '' && this.agentMap[row.agentId]) ? this.agentMap[row.agentId] : (row.agentId === '' ? 'On Demand' : '-'),
        }));

      if (!this.agentsLoaded) {
        forkJoin({
          agents: this.restService.getAgents(this.projectId),
          testPlan: this.restService.getTestPlanById(
            this.projectId,
            this.automationProjectId,
            section?.testPlanId,
          ),
        }).subscribe({
          next: ({ agents, testPlan }) => {
            (agents?.data ?? []).forEach((agent: any) => {
              this.agentMap[agent.id] = agent.name;
            });
            this.agentsLoaded = true;
            section.tableRows = mapRows(testPlan);
            section.loading = false;
          },
          error: (error) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });
      } else {
        this.restService
          .getTestPlanById(
            this.projectId,
            this.automationProjectId,
            section?.testPlanId,
          )
          .subscribe({
            next: (response) => {
              section.tableRows = mapRows(response);
              section.loading = false;
            },
            error: (error) => {
              console.error("Error fetching section data:", error);
              section.tableRows = [];
              section.loading = false;
            },
          });
      }
    } else if (typeId === 'selected_test_plans_and_scripts_accordion') {
      // Read-only preview — data is already embedded in the section as testScripts.
      // No BE call needed. Agent names are resolved from agentMap (already loaded).
      const browserIconStyle = { width: '22px', height: '22px', 'object-fit': 'contain' };
      const browserValueMap: Record<string, { value: string; style: any }> = {
        chrome: { value: 'assets/images/virtusa-icons/chrome.svg', style: browserIconStyle },
        firefox: { value: 'assets/images/virtusa-icons/firefox.svg', style: browserIconStyle },
        edge: { value: 'assets/images/virtusa-icons/edge.svg', style: browserIconStyle },
      };

      section.tableColumns = [
        {
          key: 'name',
          display_name: 'Test Case Name',
          headerStyles: { width: '40%' },
        },
        {
          key: 'version',
          display_name: 'Version',
          headerStyles: { width: '20%' },
        },
        {
          key: 'agentName',
          display_name: 'Execution Engine/Device',
          headerStyles: { width: '28%' },
        },
        {
          key: 'browser',
          display_name: 'Browser',
          headerStyles: { width: '12%' },
          _config: {
            type: 'image',
            displayInColumn: false,
            isConditional: true,
            targetKey: 'browserKey',
            value: browserValueMap,
          },
        },
      ];

      const rawScripts: any[] = (section as any).testScripts ?? [];

      const mapScripts = () => {
        section.tableRows = rawScripts.map((row: any) => ({
          ...row,
          browserKey: row.browser?.toLowerCase?.() ?? '',
          agentName: this.getAgentName(row),
        }));
        section.loading = false;
      };

      // If agentMap is not loaded yet, fetch agents first then map; otherwise map immediately.
      if (!this.agentsLoaded && rawScripts.some((r: any) => r.agentId)) {
        this.restService.getAgents(this.projectId).subscribe({
          next: (agents) => {
            (agents?.data ?? []).forEach((agent: any) => {
              this.agentMap[agent.id] = agent.name;
            });
            this.agentsLoaded = true;
            mapScripts();
          },
          error: () => mapScripts(),
        });
      } else {
        mapScripts();
      }
    } else if (typeId === 'execution_test_script_list_accordion') {
      // Rows are already pre-populated at response time.
      // Sync the latest executionEngineOptions so the section_dropdown has options
      // even if this section expands before loadExecutionEngines() resolves.
      if (section.tableRows?.length) {
        section.tableRows.forEach((row: any) => {
          row.engineSections = this.executionEngineOptions;
          row.browserOptions = this.browserOptions.map(o => o.value);
          // Apply the currently selected global engine/browser so rows created
          // before the user changed the toolbar dropdown pick up the correct value.
          if (!row.agentId || row.agentId === '') {
            row.agentId = this.testExecutionEngine ?? 'onDemand';
          }
          if (!row.browser) {
            row.browser = this.testExecutionBrowser ?? null;
          }
        });
        section.tableRows = [...section.tableRows];
      }
      section.loading = false;
    } else if (typeId === "execution_test_plan_list_accordion") {
      // Rows are pre-populated at SSE response time (testScripts embedded in each section).
      // Inject the latest executionEngineTableOptions (nested Devices/Engines) and browserOptions into each row,
      // guarding against the async loadExecutionEngines() race.
      if (section.tableRows?.length) {
        section.tableRows.forEach((row: any) => {
          // Default to 'On Demand' agent when no agent is assigned or empty string
          if (!row.agentId || row.agentId === '') {
            row.agentId = 'onDemand';
          }
          row.engineSections = this.executionEngineTableOptions;
          row.browserOptions = this.browserOptions.map(o => o.value);
        });
        section.tableRows = [...section.tableRows];
      }
      section.loading = false;
    } else if (typeId === 'mappings_accordion') {
      section.tableColumns = [
        {
          key: 'testCase',
          display_name: 'Test Case',
          headerStyles: {
            background: '#1E2235',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600'
          },
          _config: {
            type: 'double-line-data',
            targetKey: {
              firstLine: 'testCaseID',
              secondLine: 'testScenario'
            }
          }
        },
        {
          key: 'method',
          display_name: 'Method & Source Code Path',
          headerStyles: {
            background: '#1E2235',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600'
          },
          _config: {
            type: 'double-line-data',
            style: {
              gap: '8px'
            },
            prefixImage: {
              isStatic: true,
              url: 'assets/images/virtusa-icons/code.png',
              style: {
                width: '16px',
                marginRight: '6px',
                marginLeft: '2px'
              }
            },
            suffixImage: {
              isStatic: true,
              url: 'assets/images/virtusa-icons/report-gray.png',
              style: {
                height: '14px',
                width: '12px',
                marginRight: '12px',
                marginLeft: '2px'
              }
            },
            targetKey: {
              firstLine: 'methodName',
              secondLine: 'sourceCodePath'
            }
          }
        },
        {
          key: 'action',
          display_name: 'Actions',
          headerStyles: {
            background: '#1E2235',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            width: '120px'
          },
          _config: {
            type: 'link',
            name: 'View Details',
            actionKey: 'mapping_view'
          }
        }
      ];

      this.restService.getMappingDetailsById(section.mappingId as string)
        .subscribe({
          next: (response: any) => {
            console.log("Fetched section data:", response?.data);
            section.tableRows = response?.data;
            section.loading = false;
          },
          error: (error: any) => {
            console.error("Error fetching section data:", error);
            section.tableRows = [];
            section.loading = false;
          },
        });

    }
  }

  private getAgentName(row: any): string {
    if (row.agentId === '' || row.agentId === 'onDemand') {
      return 'On Demand';
    }

    if (row.deviceId) {
      return this.deviceMap[row.deviceId] || '-';
    }

    if (row.agentId) {
      return this.agentMap[row.agentId] || '-';
    }

    return '-';
  }

  private getExecutionTestPlanListAccordionColumns(): any[] {
    return [
      {
        display_name: "Test Case Name",
        key: "name",
        headerStyles: { width: "40%" },
      },
      {
        display_name: "Version",
        key: "version",
        headerStyles: { width: "20%" },
        _config: {
          type: "multi-select",
          displayInColumn: false,
          optionsKey: "versionOptions",  // resolved per-row from the versions array
          placeholder: "Select version",
        },
      },
      {
        display_name: "Execution Engine/ Device",
        key: "agentId",
        headerStyles: { width: "25%" },
        _config: {
          type: "menu_dropdown",
          targetKey: "agentId",           // pre-selects using row.agentId from the API
          targetOptions: "engineSections", // per-row grouped options from executionEngineOptions
          componentInputs: {
            config: tableTestAgentConfig,
          },
        },
      },
      {
        display_name: "Browser",
        key: "browser",
        headerStyles: { width: "10%" },
        _config: {
          type: "browser-select",
          optionsKey: "browserOptions",
          imageMap: {
            Chrome: "assets/images/virtusa-icons/chrome.svg",
            Firefox: "assets/images/virtusa-icons/firefox.svg",
            Edge: "assets/images/virtusa-icons/edge.svg",
          },
        },
      },
    ];
  }

  /**
   * Groups a flat array of test scripts (from the BE) into AccordionSection[]
   * keyed by scriptGroupName. Pre-populates tableColumns and tableRows so no
   * additional API call is needed on section expand.
   * engineSections is intentionally omitted here and injected lazily in
   * onSectionExpand so the executionEngineOptions async load race is handled.
   */
  private groupTestScriptsToSections(flatScripts: any[]): AccordionSection[] {
    const groupMap = new Map<string, { id: string; name: string; scripts: any[] }>();

    for (const script of flatScripts) {
      const groupId = script.scriptGroupId || '_ungrouped';
      const groupName = script.scriptGroupName || 'Other';
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, { id: groupId, name: groupName, scripts: [] });
      }
      groupMap.get(groupId)!.scripts.push(script);
    }

    const columns = this.getExecutionTestScriptListAccordionColumns();

    return Array.from(groupMap.values()).map(group => ({
      id: group.id,
      title: group.name,
      name: group.name,
      testcaseCount: group.scripts.length,
      itemCount: group.scripts.length,
      tableColumns: columns,
      // engineSections will be injected on first expand via onSectionExpand
      tableRows: group.scripts.map((row: any) => ({
        ...row,
        // Build a single-option version dropdown from the scalar version string
        versionOptions: row.version
          ? [{ label: row.version, value: row.version }]
          : [],
        // engineSections deliberately empty here — filled on expand
        engineSections: [],
        // Browser options injected so the browser-select panel has items
        browserOptions: this.browserOptions.map(o => o.value),
      })),
      loading: false,
      expanded: false,
    }));
  }

  private getExecutionTestScriptListAccordionColumns(): any[] {
    return [
      {
        display_name: '',
        key: 'checkbox',
        showExpand: false,
        noBorderRight: true,
        headerStyles: { 'margin-left': '5px' },
        _config: {
          type: 'checkbox',
          displayInColumn: true,
        },
      },
      {
        display_name: 'Test Case Name',
        key: 'name',
        headerStyles: { width: '40%' },
      },
      {
        display_name: 'Version',
        key: 'version',
        headerStyles: { width: '20%' },
        _config: {
          type: 'multi-select',
          displayInColumn: false,
          optionsKey: 'versionOptions',
          placeholder: 'Select version',
        },
      },
      {
        display_name: 'Execution Engine/ Device',
        key: 'agentId',
        headerStyles: { width: '25%' },
        _config: {
          type: 'menu_dropdown',
          targetKey: 'agentId',
          targetOptions: 'engineSections',
          componentInputs: { config: tableTestAgentConfig },
        },
      },
      {
        display_name: 'Browser',
        key: 'browser',
        headerStyles: { width: '15%' },
        _config: {
          type: 'browser-select',
          optionsKey: 'browserOptions',
          imageMap: {
            Chrome: 'assets/images/virtusa-icons/chrome.svg',
            Firefox: 'assets/images/virtusa-icons/firefox.svg',
            Edge: 'assets/images/virtusa-icons/edge.svg',
          },
        },
      },
    ];
  }

  paramTableData(item: any) {
    return (item ?? []).map((item: any) => {
      const clonedItem = { ...item };
      clonedItem.paramType = "-";
      clonedItem.paramName = "-";

      const children = (clonedItem.scriptParameters ?? [])
        .filter((param: any) => param.active)
        .map((param: any) => ({
          inputOutput: param.type === "INPUT" ? "Input" : "Output",
          paramName: param.name || "-",
          paramType: param.dataType || "-",
        }));

      return {
        ...clonedItem,
        ...(children.length > 0 ? { children } : {}),
        scriptParameters: undefined,
      };
    });
  }

  /**
   * Handle table row selection
   * @param event - Contains section and selected rows
   */
  onTableRowSelect(event: {
    section: AccordionSection;
    selectedRows: any;
  }): void {
    console.log("Selected rows from section:", event.section.testSuiteName);
    console.log("Selected rows:", event.selectedRows);

    // Test-execution views manage their own row state — never send chat prompts.
    if (this.TEST_EXECUTION_ACCORDION_IDS.has(event.section?.typeId ?? '')) {
      return;
    }

    if (event?.section?.type === "SCRIPT") {
      this.message = event.selectedRows?.data?.length
        ? `Generate script for:
      TestScriptGroup: ${event.section.name} ,
      TestScript: ${event.selectedRows?.data[0]?.name}`
        : "";
    } else if (this.response?.id === "tcg_test_suite_list") {
      this.message = event.selectedRows?.data?.length
        ? `Generate test script for:
            TestSuite: ${event.section.testSuiteName} ,
            TestCase: ${event.selectedRows?.data[0]?.testCaseId}`
        : "";
    } else if (event.selectedRows.type === "rowSelect") {
      let rows = event.selectedRows.data.map((ele: any) => {
        return {
          ...ele,
          chipName: ele.testCaseId,
          chipValue: ele.testCaseId,
        };
      });
      this.chatComponent.selectedRows = rows;
      this.message = rows?.length
        ? `Test suite is : ${event.section.testSuiteName}.` +
        this.getDefaultMesage(this.response?.result[0].id)
        : this.defaultMessageMap["tdg_test-case-select"];
    }
  }

  /**
   * Handle table actions (button clicks, etc.)
   * @param event - Contains section and action data
   */
  onTableActions(event: { section?: AccordionSection; action: any }): void {
    console.log("Table action from section:", event?.section?.testSuiteName);
    console.log("Action:", event?.action);

    // Test-execution views handle their own in-row dropdowns — never trigger chat.
    if (this.TEST_EXECUTION_ACCORDION_IDS.has(event?.section?.typeId ?? '')) {
      return;
    }

    // Check if action is for viewing objects from application_list
    if (
      event.action?.data?.column?.key === "objectsCount" &&
      event?.action?.data?.row?.uiObjects
    ) {
      this.handleViewObjects(event?.action?.data.row);
    } else if (event?.action?.data?.columnConfig?.name === "view script") {
      console.log("View script action for row:", event?.action?.data.row);
      this.openEditorView(event?.action?.data.row);
    } else if (event?.action?.type === "link" && event?.action?.data?.column?._config?.actionKey === 'mapping_view') {
      this.testCaseDetailModal(event?.action?.data?.row);
    } else {
      // Existing logic for custom table preview
      this.chatComponent.sendInput = `Test Suite Name : ${event?.section?.testSuiteName} . Load Custom Table data for Test Case Id : ${event.action.data?.row?.testCaseId}`;
      this.chatComponent.send();
    }
  }

  // ── Test Execution Plan accordion handlers ──────────────────────────────────

  onTestExecutionCheckboxChange(event: {
    section: AccordionSection;
    checked: boolean;
  }): void {

    if(event.section?.id === 'mappings_accordion') {
      return;
    }

    if (event.checked) {
      if (!this.selectedExecutionPlans.some((s) => s.id === event.section.id)) {
        this.selectedExecutionPlans.push(event.section);
      }
    } else {
      this.selectedExecutionPlans = this.selectedExecutionPlans.filter(
        (s) => s.id !== event.section.id,
      );
    }
  }

  /**
   * IDs whose accordion uses section-level Select All (toggles accordion-header
   * checkboxes). Add new IDs here as the backend introduces them — no template
   * changes required.
   */
  private readonly SECTION_LEVEL_ACCORDION_IDS = new Set<string>([
    "execution_test_plan_list_accordion", // section-level: Select All toggles accordion-header checkboxes
  ]);

  /** Accordion IDs that belong to the test-execution flow — row/action events
   *  from these views must never trigger chat prompt messages. */
  private readonly TEST_EXECUTION_ACCORDION_IDS = new Set<string>([
    'execution_test_script_list_accordion',
    'execution_test_plan_list_accordion',
    'selected_test_plans_and_scripts_accordion',
  ]);

  /** Returns per-id config for the shared accordion test execution template. */
  getTestExecConfig(id: string): {
    selectAllSectionsOnly: boolean;
    showSectionCheckbox: boolean;
  } {
    const isSectionLevel = this.SECTION_LEVEL_ACCORDION_IDS.has(id);
    return {
      selectAllSectionsOnly: isSectionLevel, // true → Select All toggles section checkboxes
      showSectionCheckbox: isSectionLevel, // true → show checkbox on accordion header row
    };
  }

  /** Unified Select All handler — behaviour determined by item id, not type. */
  onUnifiedTestExecSelectAll(checked: boolean, id: string): void {
    if (this.SECTION_LEVEL_ACCORDION_IDS.has(id)) {
      // Section-level: keep selectedExecutionPlans in sync
      const item = this.response?.result?.find((r: any) => r.id === id);
      this.selectedExecutionPlans = checked ? [...(item?.data ?? [])] : [];
    } else {
      // Row-level: rows are toggled inside the accordion; track state for downstream use
      this.isTestExecutionRowSelectAll = checked;
    }
  }

  /** Loads execution engine options and devices grouped by type with nested sub-sections. */
  private loadExecutionEngines(): void {
    forkJoin({
      agents: this.restService.getAgentsWithStatus(this.projectId),
      devices: this.restService.getDevices(this.projectId),
    }).subscribe({
      next: ({ agents, devices }) => {
        const localAgents: any[] = [];
        const cloudAgents: any[] = [];
        const localDevices: any[] = [];
        const remoteDevices: any[] = [];

        // Process agents
        (agents?.data ?? [])
          .filter((a: any) => a.active)
          .forEach((agent: any) => {
            const opt = {
              label: agent.name,
              icon:
                agent.status === 'OFFLINE'
                  ? 'assets/images/agent/agent-dead.svg'
                  : 'assets/images/agent/agent-alive.svg',
              value: agent.id,
              agentType: agent.type,
            };
            if (agent.type === 'LOCAL') {
              localAgents.push(opt);
            } else {
              cloudAgents.push(opt);
            }
          });

        // Process devices grouped by environment (LOCAL/REMOTE)
        (devices?.data ?? []).forEach((device: any) => {
          const deviceOpt = {
            label: device.name,
            value: device.id,
            deviceType: device.environment,
          };
          // Populate deviceMap for name resolution
          this.deviceMap[device.id] = device.name;
          if (device.environment?.toUpperCase() === 'LOCAL') {
            localDevices.push(deviceOpt);
          } else {
            remoteDevices.push(deviceOpt);
          }
        });

        // Build 3-level hierarchy structure for general use (toolbar, script accordion)
        this.executionEngineOptions = [
          // Devices section with Local/Remote sub-sections
          {
            label: 'Devices',
            type: 'devices',
            options: [
              ...(localDevices.length
                ? [{ label: 'Local', options: localDevices }]
                : []),
              ...(remoteDevices.length
                ? [{ label: 'Remote', options: remoteDevices }]
                : []),
            ],
          },
          // Execution Engines section with On Demand/Local/Cloud sub-sections
          {
            label: 'Execution Engines',
            type: 'agents',
            options: [
              {
                label: 'On Demand',
                options: [
                  {
                    label: 'On Demand',
                    icon: 'assets/images/agent/agent-alive.svg',
                    value: 'onDemand',
                    agentType: 'CLUSTER',
                  },
                ],
              },
              ...(localAgents.length
                ? [{ label: 'Local', options: localAgents }]
                : []),
              ...(cloudAgents.length
                ? [{ label: 'Cloud', options: cloudAgents }]
                : []),
            ],
          },
        ];

        // Build nested structure for test execution plan table dropdown: Devices and Execution Engines as main sections
        this.executionEngineTableOptions = [
          // Devices section with Local/Remote sub-sections - always show even if empty
          {
            label: 'Devices',
            type: 'devices',
            options: [
              ...(localDevices.length
                ? [{ label: 'Local', options: localDevices }]
                : []),
              ...(remoteDevices.length
                ? [{ label: 'Remote', options: remoteDevices }]
                : []),
            ],
          },
          // Execution Engines section with On Demand/Local/Cloud sub-sections
          {
            label: 'Execution Engines',
            type: 'agents',
            options: [
              {
                label: 'On Demand',
                options: [
                  {
                    label: 'On Demand',
                    icon: 'assets/images/agent/agent-alive.svg',
                    value: 'onDemand',
                    agentType: 'CLUSTER',
                  },
                ],
              },
              ...(localAgents.length
                ? [{ label: 'Local', options: localAgents }]
                : []),
              ...(cloudAgents.length
                ? [{ label: 'Cloud', options: cloudAgents }]
                : []),
            ],
          },
        ];
      },
      error: () => {
        this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_DEVICE_LOAD_FAILURE });
      },
    });
  }

  onTestExecutionDefaultToggle(isDefault: boolean): void {
    this.testExecutionIsDefault = isDefault;
  }

  onTestExecutionEngineChange(engine: any): void {
    this.testExecutionEngine = engine;
  }

  onTestExecutionBrowserChange(browser: any): void {
    this.testExecutionBrowser = browser;
  }

  onTestExecutionSave(payload: {
    sections: AccordionSection[];
    isDefault: boolean;
    engine: any;
    browser: any;
    typeId?: string;
  }): void {

    let isAllSelected = this.response.result[0]?.data?.length === this.selectedExecutionPlans.length;

    if (isAllSelected && payload.isDefault && (!payload.engine || !payload.browser)) {
      this.toaster.pop({ type: 'warning', text: MessageEnum.SELECT_BROWSER_AND_EXECUTION_ENGINE_BEFORE_SAVING });
      return;
    }

    if ((isAllSelected && !payload.isDefault) || !this.isTestExecutionRowSelectAll) {
      // Validate that every row in the selected sections has a browser assigned
      const sectionsToCheck = payload.sections?.filter((item: any) => item.isSelected) ?? [];
      const hasMissingBrowser = sectionsToCheck.some((section: any) =>
        (section.tableRows ?? []).some((row: any) => !row.browser)
      );
      if (hasMissingBrowser) {
        this.toaster.pop({ type: 'warning', text: MessageEnum.SELECT_BROWSER_FOR_ALL_TEST_CASES });
        return;
      }
    }

    // ── Ad-Hoc save (execution_test_script_list_accordion) ───────────────────
    if (payload.typeId === 'execution_test_script_list_accordion') {
      this.saveAdHocPlan(payload.sections);
      return;
    }

    // ── Test-Plan save (execution_test_plan_list_accordion) ──────────────────
    // Only include accordion sections the user has checked
    const selectedSections = payload.sections.filter(s => s.isSelected);

    if (selectedSections.length === 0) {
      this.toaster.pop({ type: 'warning', text: MessageEnum.SELECT_AT_LEAST_ONE_TEST_PLAN_BEFORE_SAVING });
      return;
    }

    this.isSaveLoading = true;

    const body = {
      project_id: this.projectId,
      thread_id: this.chatComponent?.threadId || this.threadId,
      save_type: 'TestPlan',
      test_plans: selectedSections.map((section) => ({
        id: section.testPlanId,
        name: section.name || section.testPlanName,
        automationProjectId: this.automationProjectId,
        createdAt: section.createdAt,
        active: true,
        testScripts: (section.tableRows ?? []).map((row: any) => ({
          sequence: row.sequence ?? 0,
          id: row.id,
          scriptId: row.scriptId,
          name: row.name,
          version: row.version,
          versions: row.versions ?? [],
          increment: row.increment ?? 0,
          // agentId: row.agentId ?? 'onDemand',
          browser: row.browser,
          tcgTestCaseResultId: row.tcgTestCaseResultId,
          ...(row.deviceId ? {
            executionType: 'MOBILE',
            deviceId: row.deviceId,
          } : {
            agentId: row.agentId ?? 'onDemand',
          }),
        })),
      })),
    };

    this.restService.saveTestPlanExecutionCache(body).subscribe({
      next: () => {
        this.isSaveLoading = false;
        this.toaster.pop({ type: 'success', text: MessageEnum.TEST_PLAN_EXECUTION_SETTINGS_SAVE_SUCCESS });
      },
      error: () => {
        this.isSaveLoading = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_PLAN_EXECUTION_SETTINGS_SAVE_FAILURE });
      },
    });
  }

  /**
   * Builds the Ad-Hoc payload from checkboxed rows across all script-group
   * sections and posts it to the cache endpoint.
   *
   * Only sections that have at least one selected row (row.select === true)
   * are included in adhoc_plans. An error toast is shown when nothing is
   * selected so the user gets clear feedback before anything is sent.
   */
  private saveAdHocPlan(sections: AccordionSection[]): void {
    const adhocPlans = sections
      .map((section) => {
        const selectedScripts = (section.tableRows ?? []).filter(
          (row: any) => row.select === true,
        );
        return { section, selectedScripts };
      })
      .filter(({ selectedScripts }) => selectedScripts.length > 0)
      .map(({ section, selectedScripts }) => ({
        id: section.id,
        name: section.name,
        automationProjectId: this.automationProjectId,
        createdAt: section.createdAt ?? new Date().toISOString(),
        active: true,
        testScripts: selectedScripts.map((row: any, index: number) => ({
          sequence: row.sequence ?? index,
          id: row.id,
          scriptId: row.scriptId,
          name: row.name,
          version: row.version,
          versions: row.versions ?? [],
          increment: row.increment ?? 0,
          agentId: row.agentId ?? this.testExecutionEngine ?? 'onDemand',
          browser: row.browser ?? this.testExecutionBrowser ?? null,
          tcgTestCaseResultId: row.tcgTestCaseResultId ?? null,
        })),
      }));

    if (adhocPlans.length === 0) {
      this.toaster.pop({
        type: 'warning',
        text: MessageEnum.SELECT_AT_LEAST_ONE_TEST_SCRIPT_BEFORE_SAVING,
      });
      return;
    }

    this.isSaveLoading = true;

    const body = {
      project_id: this.projectId,
      thread_id: this.chatComponent?.threadId || this.threadId,
      save_type: 'AdHoc' as const,
      adhoc_plans: adhocPlans,
    };

    this.restService.saveAdHocExecutionCache(body).subscribe({
      next: () => {
        this.isSaveLoading = false;
        this.toaster.pop({
          type: 'success',
          text: MessageEnum.AD_HOC_TEST_SCRIPTS_SAVE_SUCCESS,
        });
      },
      error: () => {
        this.isSaveLoading = false;
        this.toaster.pop({
          type: 'error',
          text: MessageEnum.AD_HOC_TEST_SCRIPTS_SAVE_FAILURE,
        });
      },
    });
  }

  // ── Application list accordion handlers ──────────────────────────────────────

  /**
   * Handle section checkbox change in accordion
   * @param event - Contains section and checked status
   */
  onSectionCheckboxChange(event: {
    section: AccordionSection;
    checked: boolean;
  }): void {
    const applicationItem = this.response?.result?.find(
      (item: any) => item.id === "application_list",
    );

    if (applicationItem && applicationItem.data) {
      // unselect all sections to ensure only one can be selected
      applicationItem.data.forEach((section: AccordionSection) => {
        section.isSelected = false;
      });

      // if the checkbox is being checked (not unchecked), select only this section
      if (event.checked) {
        event.section.isSelected = true;
      }
    }

    if(this.response.result[0]?.id === 'mappings_accordion') {
      let data = this.response?.result[0].data.filter((ele: any) => ele.isSelected);
      this.chatComponent.selectedRows = data.map((row: any) => ({
        ...event,
        chipName: row?.mappingName ? row.mappingName : row.name,
        chipValue: row?.mappingName ? row.mappingName : row.name,
      }));
    } else {

    // Set the selected application with formatted data
    this.chatComponent.selectedRows = event.checked
      ? [
        {
          ...event.section,
          chipName: event?.section?.name,
          chipValue: event?.section?.name
        },
      ]
      : [];
    }

    this.message = this.chatComponent.selectedRows?.length
      ? this.getDefaultMesage(this.response?.result[0].id, this.chatComponent.selectedRows?.length)
      : "";
    this.chatComponent.sendInput = this.message;
  }

  /**
   * Handle viewing UI objects for a page
   * @param pageData - Page data containing uiObjects
   */
  handleViewObjects(pageData: any): void {
    console.log("Viewing objects for page:", pageData.name);
    console.log("Page Data -->>:", pageData);

    // Open modal first with loading state
    let modalRef = this.modalService.open(ObjectViewModalComponent, {
      centered: true,
      size: "xl",
      backdrop: "static",
    });

    modalRef.componentInstance.configs = {
      data: [],
      columns: [
        { key: "name", display_name: "UI Object Name" },
        {
          key: "locators",
          display_name: "Locators",
          _config: {
            type: "image_with_text",
            prefixImage: {
              targetKey: "type",
              values: {
                Firefox: "assets/images/virtusa-icons/firefox.png",
                Chrome: "assets/images/virtusa-icons/chrome.png",
                Edge: "assets/images/virtusa-icons/edge.png",
                android: "assets/images/virtusa-icons/android.svg",
                ios: "assets/images/virtusa-icons/ios.svg",
                Android: "assets/images/virtusa-icons/android.svg",
                iOS: "assets/images/virtusa-icons/ios.svg",
              },
            },
            suffixImage: {
              targetKey: "selfHealed",
              values: { true: "assets/images/virtusa-icons/shl.png" },
            },
            value: "locator",
          },
        },
      ],
      title: "Objects & locators",
    };
    modalRef.componentInstance.isLoading = true;

    // Load UI objects and update modal when data arrives
    this.loadUiObjects(pageData?.id, modalRef);
  }

  isBase64(str: string, enc: any): boolean {
    // Basic validation: must match base64 pattern and length must be divisible by 4
    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;

    if (!str || str.length % 4 !== 0 || !base64Regex.test(str)) {
      return false;
    }

    try {
      enc.decode(str);
      return true;
    } catch (e) {
      return false;
    }
  }

  loadUiObjects(pageId: string, modalRef?: any) {
    this.agenticAiService
      .loadUiObjects(
        this.projectId,
        this.automationProjectId,
        this.selectedApplicationId,
        pageId,
      )
      .subscribe({
        next: (data) => {
          const enc = new Base64();
          this.uiObjects = data.map((uiObject: any) => ({
            ...uiObject,
            locators: uiObject.locators.map((locator: any) => ({
              ...locator,
              locator: this.isBase64(locator.locator, enc)
                ? enc.decode(locator.locator)
                : locator.locator,
            })),
          }));

          // Update modal with loaded data
          if (modalRef) {
            modalRef.componentInstance.configs.data = this.uiObjects;
            modalRef.componentInstance.isLoading = false;
          }
        },
        error: (error) => {
          console.error("Error loading UI objects:", error);
          // Update modal to show error state
          if (modalRef) {
            modalRef.componentInstance.isLoading = false;
            modalRef.componentInstance.configs.data = [];
          }
        },
      });
  }

  onTdgTableChange(event: any) {
    console.log("selected row TDG -->", event);
    this.selectedRowsTDG = event?.data.length > 0 ? event?.data : [];
  }

  getTransformed(data: any) {
    // Transform rawData array to objects
    return data?.rawData?.map((row: any[], index: number) => {
      let obj: any = { id: index };
      data?.columns?.forEach((col: string, colIndex: number) => {
        obj[col] = row[colIndex];
      });
      return obj;
    });
  }

  transformBackToOriginal(data: any) {
    // Transform objects back to rawData array, ignoring 'rowSelect', 'edit', and 'add' columns
    const columnsToInclude = data.columns.filter(
      (col: any) =>
        col.key !== "edit" && col.key !== "rowSelect" && col.key !== "add",
    );

    return {
      rawData: data.data.map((item: any) => {
        return columnsToInclude.map((col: any) => {
          const value = item[col.key];
          return value !== undefined && value !== null ? value : "";
        });
      }),
      columns: columnsToInclude.map((col: any) => col.key),
    };
  }

  onTableSave(data: any, originalData: any) {
    this.saveData(data, originalData);
  }

  onSelectedTab(event: any) {
    console.log("selected table TDG -->", event);
    this.selectedTabTDG = event?.db_name ? event?.db_name : "";
  }

  onTdgActionChange(type: string, data: any) {
    switch (type) {
      case "save":
        this.message = "";
        this.syncCustomData(data?.parseFileId, false);
        break;

      case "re-generate":
        this.message =
          `Test Case ID: ${this.selectedTabTDG}, ` +
          `Regenerate data in Column: {column_Name}, ` +
          `Action: {Action}`;
        this.chatComponent.sendInput = this.message;
        break;

      case "insert-row":
        this.message =
          `Test Case ID: ${this.selectedTabTDG}, ` +
          `Action: Insert new data,` +
          `Number of rows: 1`;
        this.chatComponent.sendInput = this.message;
        break;

      case "delete":
        this.message = "Delete selected rows of " + this.selectedTabTDG;
        break;

      default:
        break;
    }
  }

  syncCustomData(parseFileId: string, isAfterProgress?: boolean) {
    let parseFileIdLocal;
    if (!isAfterProgress) {
      this.multiTabComponent.isSaving = true;
      parseFileIdLocal =
        parseFileId || localStorage.getItem("parseFileId") || "";
    } else {
      parseFileIdLocal = parseFileId;
    }
    this.restService
      .syncCustomTables(parseFileIdLocal, this.projectId)
      .subscribe(
        (response: any) => {
          if (!isAfterProgress) {
            this.multiTabComponent.isSaving = false;
          }
          this.persistDataBatch(response?.updated_ids);
        },
        (error) => {
          console.error("Error syncing custom tables:", error);
          this.multiTabComponent.isSaving = false;

          this.toaster.pop({
            text: MessageEnum.SYNCING_CUSTOM_TABLE_ERROR,
            type: "error",
          });
        },
      );
  }

  persistDataBatch(ids: any) {
    this.restService
      .persistDataBatch(ids, this.projectId, this.automationProjectId)
      .subscribe(
        (response: any) => {
          if (response.success) {
            console.log("Data persist success!", ids);
          } else {
            console.error("Data persist failure!", ids);
          }
        },
        (error) => {
          console.error("Error persisting custom tables:", error);
        },
      );
  }

  saveData(formattedData: any, originalData: any) {
    const clonedData = structuredClone(formattedData);

    clonedData.columns.splice(
      clonedData.columns.findIndex((col: any) => col.key === "edit"),
      1,
    );
    const dataColumns = clonedData.columns
      .filter((col: any) => col.key !== "rowSelect" && col.key !== "edit")
      .map((col: any) => col.key);

    const rawData = clonedData.data.map((row: any) => {
      return dataColumns.map((colKey: any) => {
        const value = row[colKey];
        return value !== undefined && value !== null ? value : "";
      });
    });

    const payload = {
      CustomTable: {
        projectId: originalData.projectId,
        tableId: originalData.id,
        testCaseId: originalData.testCaseId,
        testSuite: originalData.testSuite,
        tables: [
          {
            tableName: originalData.tableData.testCaseId || "CustomTable",
            columns: dataColumns,
            rawData: rawData,
          },
        ],
      },
    };

    this.restService.updateCustomTableData(payload).subscribe(
      (response: any) => {
        this.toaster.pop({
          text: MessageEnum.TEST_CASE_DATA_SAVE_SUCCESS,
          type: "success",
        });
        // this.chatComponent.sendInput = "Reload data for Test Case ID: " + originalData.testCaseId;
        this.chatComponent.sendInput = `Test Suite Name : ${originalData.testSuite} . Reload Custom Table data for Test Case Id : ${originalData?.testCaseId}`;
        this.chatComponent.send();
        this.persistDataBatch([originalData.id]);
        // this.chatComponent.sendInput = "Custom Table Updated Successfully.";
        // this.chatComponent.send();
      },
      (error) => {
        console.error("Error saving test case data:", error);
        this.toaster.pop({
          text: error?.error?.message || MessageEnum.TEST_CASE_DATA_SAVE_FAILURE,
          type: "error",
        });
      },
    );
  }

  /**
   * Opens the editor view in a new tab with isAgentic parameter set to true
   */
  openEditorView(data: any, isAgentic: boolean = true): void {
    let scriptId = data?.id ? data.id : data?.scriptId;
    if (!this.projectId || !this.automationProjectId || !scriptId) {
      console.error("Missing required IDs to open editor view");
      return;
    }

    this.restService.getEditorStatus(this.dataStore?.data?.id, this.automationProjectId, scriptId).subscribe({
      next: (response) => {
        if (response?.data?.canOpen) {
          this.setEditorOpenedStatus(this.dataStore?.data?.id, this.automationProjectId, scriptId);
          const newUrl = this.buildAppUrl(
            `project/${this.projectId}/automation/${this.automationProjectId}/script/${scriptId}/editor-view?isAgentic=${isAgentic}`
          );
          window.open(newUrl, "_blank");
        } else {
          this.toaster.pop({ type: 'warning', text: response?.data?.message });
          this.setEditorOpenedStatus(this.dataStore?.data?.id, this.automationProjectId, scriptId);
        }
      },
      error: (error) => {
        console.error("Error checking editor status:", error);
      }
    });
  }

  setEditorOpenedStatus(projectId: string, automationProjectId: string, scriptId: string) {
    this.restService.setEditorOpenStatus(projectId, automationProjectId, scriptId).subscribe({
      next: (response) => {
        console.log(response);
      },
      error: (error) => {
        console.error("Error setting editor status:", error);
      }
    });
  }

  loadAIRecordActivities(recorderId: any) {
    this.isProgressTracking = true;
    this.isScriptProcessLogging = true;
    this.progress.percentage = 4;
    this.progress.text = "Starting script generation...";
    this.disableInput = true;
    this.processLogs = [];
    this.processLogsDefined = false;
    this.isProcessLogsExpanded = true;

    interval(10000)
      .pipe(
        switchMap(() =>
          this.restService.getAIRecordedActivitiesStatus(
            this.projectId,
            recorderId,
          ),
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error,
          );
          this.toaster.pop({
            type: "error",
            text: MessageEnum.RECORDED_ACTIVITIES_LOAD_FAILURE,
          });
          this.isProgressTracking = false;
          this.isScriptProcessLogging = false;
          this.disableInput = false;
          return EMPTY;
        }),
      )
      .subscribe((res: any) => {
        this.updateProcessLogs(res?.data?.logs);
        if (res.status === 201 && res.data?.status !== "COMPLETED") {
          if (
            res.data.status === "ERROR_Agentic_LLM_TSG" ||
            res.data.status === "ERROR_AUTOMATIC_SG"
          ) {
            this.progress.isError = true;
            this.progress.text =
              res.data.errorMessage || "Error generating script";
            this.disableInput = false;
            this.stopPolling$.next();
          } else {
            this.progress.isError = false;
            this.progress.text = "Generating script...";
            const randomIncrement = Math.floor(Math.random() * 3) + 9;
            this.progress.percentage = Math.min(
              this.progress.percentage + randomIncrement,
              90,
            );
          }
          console.log("Polling: Script is still generating...");
        } else {
          this.stopPolling$.next();
          this.progress.text = "Script generated successfully!";
          this.progress.percentage = 100;
          this.disableInput = false;

          setTimeout(() => {
            this.isProgressTracking = false;
            this.isScriptProcessLogging = false;
            this.message = "Load the generated web script";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();
          }, 2000);
        }
      });
  }

  loadMobileScriptActivities(recorderId: any) {
    this.isProgressTracking = true;
    this.isScriptProcessLogging = true;
    this.progress.percentage = 4;
    this.progress.text = "Starting script generation...";
    this.disableInput = true;
    this.processLogs = [];
    this.processLogsDefined = false;
    this.isProcessLogsExpanded = true;

    interval(10000)
      .pipe(
        switchMap(() =>
          this.restService.getMobileTaskDetail(
            this.projectId,
            recorderId,
          ),
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error,
          );
          this.toaster.pop({
            type: "error",
            text: MessageEnum.RECORDED_ACTIVITIES_LOAD_FAILURE,
          });
          this.isProgressTracking = false;
          this.isScriptProcessLogging = false;
          this.disableInput = false;
          return EMPTY;
        }),
      )
      .subscribe((res: any) => {
        this.updateProcessLogs(res?.data?.logs);
        if (res.status === 201 && res.data?.status !== "COMPLETED") {
          if (
            res.data.status === "ERROR_Agentic_LLM_TSG" ||
            res.data.status === "ERROR_AUTOMATIC_SG" ||
            res.data.status === "FAILED"
          ) {
            this.progress.isError = true;
            this.progress.text =
              res.data.errorMessage || res.data.response?.data || res.data.error || "Error generating script";
            this.progress.percentage = 100;
            this.disableInput = false;
            this.stopPolling$.next();

            setTimeout(() => {
              this.isProgressTracking = false;
              this.isScriptProcessLogging = false;
            }, 2000);
          } else {
            this.progress.isError = false;
            this.progress.text = res.data.statusMessage || "Generating script...";
            const randomIncrement = Math.floor(Math.random() * 3) + 9;
            this.progress.percentage = Math.min(
              this.progress.percentage + randomIncrement,
              90,
            );
          }
          console.log("Polling: Script is still generating...");
        } else {
          this.stopPolling$.next();
          this.progress.text = "Script generated successfully!";
          this.progress.percentage = 100;
          this.disableInput = false;

          setTimeout(() => {
            this.isProgressTracking = false;
            this.isScriptProcessLogging = false;
            this.message = "Load the generated mobile script";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();
          }, 2000);
        }
      });
  }

  private updateProcessLogs(logs: any): void {
    if (!Array.isArray(logs)) {
      return;
    }
    if (logs.length > 0) {
      this.processLogsDefined = true;
      this.processLogs = logs.map((log: any) => ({
        step: log?.step,
        thinking: log?.thinking,
        evaluation: log?.evaluation,
      }));
      return;
    }
    if (this.processLogsDefined) {
      return;
    }
  }

  get completedEvaluations(): string[] {
    if (!this.processLogs?.length) {
      return [];
    }
    const isDone = this.progress.percentage >= 100 || this.progress.isError;
    const end = isDone ? this.processLogs.length : Math.max(0, this.processLogs.length - 1);
    return this.processLogs
      .slice(0, end)
      .map((log) => (log.evaluation || '').trim())
      .filter((evaluation) => !!evaluation);
  }

  get liveThinkingText(): string {
    if (this.progress.percentage >= 100 || this.progress.isError) {
      return '';
    }
    const latest = this.processLogs[this.processLogs.length - 1];
    return this.getCleanThinking(latest?.thinking);
  }

  get showProcessLogsPanel(): boolean {
    return this.isProgressTracking && this.isScriptProcessLogging && this.processLogsDefined;
  }

  toggleProcessLogsExpanded(): void {
    this.isProcessLogsExpanded = !this.isProcessLogsExpanded;
  }

  getCleanThinking(thinking?: string): string {
    if (!thinking) {
      return '';
    }
    const cutMarkers = ['<ctrl', 'call:AgentOutput', '{"current_state"'];
    let cleaned = thinking;
    for (const marker of cutMarkers) {
      const idx = cleaned.indexOf(marker);
      if (idx > 0) {
        cleaned = cleaned.slice(0, idx);
      }
    }
    return cleaned.trim();
  }

  trackApiScriptProgress(llmApiId: any): void {
    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.text = "Starting API script generation...";
    this.progress.showCancel = true;
    this.disableInput = true;

    interval(10000)
      .pipe(
        switchMap(() =>
          this.restService.getApiLLMScriptStatus(this.projectId, llmApiId)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error tracking API script progress:', error);
          this.toaster.pop({
            type: 'error',
            text: error?.error?.message || error?.message || MessageEnum.API_SCRIPT_PROGRESS_TRACK_FAILURE
          });
          this.isProgressTracking = false;
          this.disableInput = false;
          this.progress.showCancel = false;
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        const status = res.data?.status?.toLowerCase();

        if (status === 'completed') {
          this.stopPolling$.next();
          this.progress.text = 'API script generated successfully!';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;

          setTimeout(() => {
            this.isProgressTracking = false;
            this.message = "Load the generated API script";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();
          }, 2000);
        } else if (status === 'failed' || status === 'error') {
          this.progress.isError = true;
          this.progress.text = res.data?.errorMessage || 'API script generation failed';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;
          this.stopPolling$.next();
          this.openValidationError(res.data?.errorMessage || 'API script generation failed');
          setTimeout(() => {
            this.isProgressTracking = false;
          }, 2000);
        } else if (status === 'cancelled') {
          this.stopPolling$.next();
          this.progress.text = 'API script generation cancelled';
          this.progress.percentage = 0;
          this.progress.showCancel = false;
          this.disableInput = false;

          setTimeout(() => {
            this.isProgressTracking = false;
          }, 2000);
        } else {
          this.progress.isError = false;
          this.progress.text = 'Generating API script...';
          this.progress.percentage = res.data?.completionPercentage || Math.min(
            this.progress.percentage + Math.floor(Math.random() * 3) + 6,
            87
          );
        }
      });
  }

  /**
   * Cancel API script generation
   * Stops the progress polling and updates the UI state
   */
  cancelApiScript(): void {
    this.stopPolling$.next();
    this.progress.text = 'API script generation cancelled by user';
    this.progress.percentage = 0;
    this.progress.showCancel = false;
    this.disableInput = false;

    setTimeout(() => {
      this.isProgressTracking = false;
    }, 2000);

    this.toaster.pop({
      type: 'warning',
      text: MessageEnum.API_SCRIPT_GENERATION_CANCELLED
    });
  }

  openValidationError(errorMsg: string) {
    let modalRef = this.modalService.open(ValidationWarningModalComponent, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });


    modalRef.componentInstance.configs = {
      title: "File Validation Failed",
      subTitle: "Swagger Validation Failed",
      requiredAction: "",
      errorMsg: errorMsg,
      tip: "",
      data: [],
      buttonText: "OK",
    };
  }

  getGroupTitle(group: any): string {
    return group?.groupDisplayName ?? '';
  }

  formatTrsDateRange(metadata: any): string {
    if (!metadata?.startDate || !metadata?.endDate) return '';
    const fmt = (dateStr: string) =>
      new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    return `${fmt(metadata.startDate)} - ${fmt(metadata.endDate)}`;
  }

  onTrsReleaseChange(releaseId: string, item: any): void {
    const releases: any[] = item?.data?.releases ?? [];
    const selected = releases.find((r: any) => r.value === releaseId);
    const releaseName = selected?.label ?? releaseId;
    const prompt = releaseId === 'All_Release'
      ? 'Show TRS data for all releases'
      : `Show TRS data for release: ${releaseName}`;
    this.message = prompt;
    this.chatComponent.sendInput = prompt;
    this.chatComponent.send();
  }

  onTrsReleaseChangeForMetricData(releaseId: string, item: any): void {
    const releases: any[] = item?.data?.releases ?? [];
    const selected = releases.find((r: any) => r.value === releaseId);
    const releaseName = selected?.label ?? releaseId;
    const groups = item?.data?.metricGroups ?? [];
    const groupIndex = groups.length > 1 ? this.activeGroupIndex : 0;
    const currentGroup = groups[groupIndex];
    const metrics: any[] = currentGroup?.metrics ?? [];
    const displayName = metrics.length === 1
      ? (metrics[0]?.metricName ?? currentGroup?.groupDisplayName)
      : currentGroup?.groupDisplayName;
    const prompt = releaseId === 'All_Release'
      ? `Show TRS data for ${displayName} for all releases`
      : `Show TRS data for ${displayName} for release: ${releaseName}`;
    this.message = prompt;
    this.chatComponent.sendInput = prompt;
    this.chatComponent.send();
  }

  getTileClass(size: string): string {
    return size || "col-md-12"; // Default to full width if no size is provided
  }

  isTrsMetricView(): boolean {
    const trsTypes = ['trs_all_metrics_data'];
    return this.response?.result?.some((item: any) => trsTypes.includes(item?.type)) ?? false;
  }

  trackMappingProgress(mappingId: any, isRecommendation?: boolean) {
    //this.versionId
    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.text = "Extracting source code...";
    this.progress.showCancel = false;
    this.disableInput = true;

    interval(10000)
      .pipe(
        switchMap(() =>
          this.restService.handleAsyncRequest(this.versionId)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error extracting:', error);
          this.toaster.pop({
            type: 'error',
            text: error?.error?.message || error?.message || MessageEnum.EXTRACT_SOURCE_CODE_FAILURE
          });
          this.isProgressTracking = false;
          this.disableInput = false;
          this.progress.showCancel = false;
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        const status = res.asyncReqStatus?.toLowerCase();

        if (status === 'success') {
          this.stopPolling$.next();
          this.progress.text = isRecommendation ? 'Recommendations extracted successfully!' : 'Source code extracted successfully!';
          this.progress.percentage = isRecommendation ? 100 : 50;
          this.progress.showCancel = false;
          this.disableInput = isRecommendation ? false : true;

          setTimeout(() => {
            if (isRecommendation) {
              this.isProgressTracking = false;
              this.message = "load generated recommendations";
              this.chatComponent.sendInput = this.message;
              this.chatComponent.send();
            } else {
              this.isProgressTracking = true;
              this.analyze(mappingId)
            }

          }, 2000);

        } else if (status === 'failed' || status === 'error') {
          this.progress.isError = true;
          this.progress.text = res.data?.errorMessage || 'Source code extraction failed';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;
          this.stopPolling$.next();
          setTimeout(() => {
            this.isProgressTracking = false;
          }, 2000);
        } else {
          this.progress.isError = false;
          this.progress.text = isRecommendation ? 'Extracting recommendations' : 'Extracting source code...';
          this.progress.percentage = res.data?.completionPercentage || Math.min(
            this.progress.percentage + Math.floor(Math.random() * 3) + 6,
            47
          );
        }
      });
  }

  analyze(mappingId: any) {
    this.restService.createAnalyze(mappingId).subscribe({
      next: (res: any) => {
        if (res?.status === 201) {
          this.sourceCodeMapping(mappingId, res?.asyncToken);
        }
      },
      error: (error) => {
        this.toaster.pop({
          type: 'error',
          text: error?.error?.message || error?.message || MessageEnum.ANALYZE_MAPPING_FAILURE
        });
        this.isProgressTracking = false;
        this.disableInput = false;
        this.progress.showCancel = false;
      },
    });
  }

  sourceCodeMapping(mappingId: any, token: any) {
    this.isProgressTracking = true;
    this.progress.percentage = 50;
    this.progress.text = "Generating source code mapping";
    this.progress.showCancel = true;
    this.disableInput = true;

    interval(10000)
      .pipe(
        switchMap(() =>
          this.restService.getTestCasesWithId(mappingId, token)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error generating mapping:', error);
          this.toaster.pop({
            type: 'error',
            text: error?.error?.message || error?.message || MessageEnum.GENERATE_MAPPING_FAILURE
          });
          this.isProgressTracking = false;
          this.disableInput = false;
          this.progress.showCancel = false;
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        const status = res?.data?.status?.toLowerCase();

        if (status === 'success' || status === 'completed') {
          this.stopPolling$.next();
          this.progress.text = 'Mapping created successfully!';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;

          setTimeout(() => {
            this.isProgressTracking = false;
            this.message = "Load generated source code mappings";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();

          }, 2000);

        } else if (status === 'failed' || status === 'error') {
          this.progress.isError = true;
          this.progress.text = res.data?.errorMessage || 'Mapping generation failed';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;
          this.stopPolling$.next();
          setTimeout(() => {
            this.isProgressTracking = false;
          }, 2000);
        } else {
          this.progress.isError = false;
          this.progress.text = 'Generating source code mapping';
          this.progress.percentage = res.data?.completionPercentage || Math.min(
            this.progress.percentage + Math.floor(Math.random() * 3) + 3,
            87
          );
        }
      });
  }

  testCaseDetailModal(data: any) {
    const modalRef = this.modalService.open(TestCaseDetailModalComponent, {
      centered: true,
      size: "lg",
      backdrop: 'static',
    });

    modalRef.componentInstance.configs = {
      title: "Test case " + (data?.testCaseID || data?.testCaseId || data?.testCase),
      saveBtnText: "Close",
      data: data
    };

    modalRef.result.then((result) => {
      if (result?.value) {
        console.log('Test case detail data:', result.value);
      }
    }).catch(() => { });
  }

  openMappingConfigsModal(data: any) {
    const modalRef = this.modalService.open(DataDisplayModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });

    modalRef.componentInstance.mainFields = [
      {
        key: "mappingName",
        name: "Mapping Name",
        type: "text-input",
      },
      {
        key: "githubLink",
        name: "Git Repo Link ",
        type: "text-input",
      },
      {
        key: "vectorDatabase",
        name: "Vector Database Name",
        type: "text-input",
      },
      {
        key: "llm",
        name: "LLM",
        type: "text-input",
        isLoading: true,
      }
    ];

    modalRef.componentInstance.data = { ...data, llm: 'Loading...' };
    modalRef.componentInstance.configs = {
      title: "Code Config Info",
      subTitle: "",
      type: 'grid',
      isCustom: true
    };

      // Resolve LLM connection name from store
    this.store.select(selectConnections)
      .pipe(take(1))
      .subscribe((connections: any) => {
        const llmConnections = connections?.LLM || [];
        const embeddingSourceConnectionId = data?.embeddingSourceConnection;
              
        let resolvedLlmName = '-';
        if (embeddingSourceConnectionId) {
          const matchedConnection = llmConnections.find(
          (conn: any) => conn.id === embeddingSourceConnectionId
          );
                
          if (matchedConnection) {
            resolvedLlmName = matchedConnection.connectionName || matchedConnection.name || embeddingSourceConnectionId;
          } else {
            resolvedLlmName = embeddingSourceConnectionId;
          }
        }
              
        modalRef.componentInstance.data = { ...data, llm: resolvedLlmName };
       // Update mainFields to remove loading state
        const updatedFields = modalRef.componentInstance.mainFields.map((field: any) => {
        if (field.key === 'llm') {
          return { ...field, isLoading: false };
        }
        return field;
      });
      modalRef.componentInstance.mainFields = updatedFields;
    });
    modalRef.result.then((result) => { });
  }

  trackImpactedProgress(mappingId: any) {
    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.showCancel = false;
    this.disableInput = true;

    interval(20000)
      .pipe(
        switchMap(() =>
          this.restService.getGeneratedImpactedTestcases(mappingId)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error extracting:', error);
          this.toaster.pop({
            type: 'error',
            text: error?.error?.message || error?.message || MessageEnum.IMPACTED_TESTCASES_LOAD_FAILURE
          });
          this.isProgressTracking = false;
          this.disableInput = false;
          this.progress.showCancel = false;
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        const status = res.message?.toLowerCase();

        if (status === 'success') {
          this.stopPolling$.next();
          this.progress.text = 'Impacted Test Cases generated successfully!';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;

          setTimeout(() => {
            this.isProgressTracking = false;
            this.message = "Load Identified Impacted Testcases";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();

          }, 2000);

        } else if (status === 'failed' || status === 'error') {
          this.progress.isError = true;
          this.progress.text = res.data?.errorMessage || 'Impacted Test Case generation failed';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;
          this.stopPolling$.next();
          setTimeout(() => {
            this.isProgressTracking = false;
          }, 2000);
        } else {
          this.progress.isError = false;
          this.progress.text = 'Generating impacted test cases...';
          this.progress.percentage = res.data?.completionPercentage || Math.min(
            this.progress.percentage + Math.floor(Math.random() * 3) + 6,
            77
          );
        }
      });
  }

  trackMostImpactedProgress(mappingId: any, reportName: any) {
    this.isProgressTracking = true;
    this.progress.percentage = 4;
    this.progress.showCancel = false;
    this.disableInput = true;
    interval(20000)
      .pipe(
        switchMap(() =>
          this.restService.getMostImpactedTestcases(mappingId, reportName)
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Error extracting:', error);
          this.toaster.pop({
            type: 'error',
            text: error?.error?.message || error?.message || MessageEnum.MOST_IMPACTED_TESTCASES_LOAD_FAILURE
          });
          this.isProgressTracking = false;
          this.disableInput = false;
          this.progress.showCancel = false;
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        const status = res.message?.toLowerCase();

        if (status === 'success') {
          this.stopPolling$.next();
          this.progress.text = 'Most mpacted Test Cases generated successfully!';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = true;

          setTimeout(() => {
            this.isProgressTracking = false;
            this.message = "Load Most Impacted Testcases";
            this.chatComponent.sendInput = this.message;
            this.chatComponent.send();

          }, 2000);

        } else if (status === 'failed' || status === 'error') {
          this.progress.isError = true;
          this.progress.text = res.data?.errorMessage || 'Most impacted Test Case generation failed';
          this.progress.percentage = 100;
          this.progress.showCancel = false;
          this.disableInput = false;
          this.stopPolling$.next();
          setTimeout(() => {
            this.isProgressTracking = false;
          }, 2000);
        } else {
          this.progress.isError = false;
          this.progress.text = 'Generating most impacted test cases...';
          this.progress.percentage = res.data?.completionPercentage || Math.min(
            this.progress.percentage + Math.floor(Math.random() * 3) + 6,
            87
          );
        }
      });
  }
}


